// Fortress market / deck balance data. Types live in `types/fortress.ts`.
//
// The card catalogue is transcribed from the physical "Fortress Cards" sheet.
// Two decks: the dark cards are the `win` deck (victory-condition fortresses),
// the light cards are the `REGULAR` deck (effect fortresses). Costs are paid in
// Obsidian, so `cost` is `[0, 0, 0, n]`. `winTiming` is set only where the card
// carries an explicit timing badge — EOR → "end-of-round", SOT → "start-of-turn".
//
// Patterns were MACHINE-EXTRACTED from the "Fortress Cards" PDF's vector art
// (each glyph snapped to the honeycomb lattice), so node placements are exact.
// For cards whose art shows two orientations either side of a divider, the
// left/top diagram is transcribed — the other side is its mirror image, which
// the matcher (and `MirrorPair` in the client) derives.
// Building shapes: circle = scout, triangle = barracks, square = mine,
// star = wild (matches any one building). The art's "X" glyphs are `markers`:
// empty nodes the Fortress occupies on completion. Thick bars on edges are
// `bridges`: Bridge pieces that are part of the pattern.
import { ZONE_HIGHEST } from "../effects/registry.js";
const EDGE = {
    NE: { q: 2, r: -1, s: -1 },
    NW: { q: -1, r: -1, s: 2 },
    S: { q: -1, r: 2, s: -1 },
    N: { q: 1, r: -2, s: 1 },
    SE: { q: 1, r: 1, s: -2 },
    SW: { q: -2, r: 1, s: 1 },
};
const A_DIRS = new Set(["NE", "NW", "S"]); // legal from an A node; rest from B
/** Walk a honeycomb path from the anchor (an A node) and return the resulting
 * node-vector offset. Steps must alternate sublattices; an illegal step throws,
 * which catches a mis-authored path at module load. */
function walk(path) {
    let v = { q: 0, r: 0, s: 0 };
    let onA = true;
    for (const d of path) {
        if (A_DIRS.has(d) !== onA) {
            throw new Error(`Illegal step ${d} from ${onA ? "A" : "B"} node in path [${path.join(",")}]`);
        }
        const e = EDGE[d];
        v = { q: v.q + e.q, r: v.r + e.r, s: v.s + e.s };
        onA = !onA;
    }
    return v;
}
/** A building at a path from the anchor. `b("wild", "NE")` = a wild one step NE. */
const b = (shape, ...at) => ({
    shape,
    at,
});
/** A Bridge on the edge crossed by the path's LAST step: `br("S")` = the edge
 * from the anchor to its S neighbour; earlier steps just walk to the edge. */
const br = (...at) => ({ at });
/** Assemble a pattern from building specs (+ marker and bridge paths). */
function pat(cells, markers = [], bridges = []) {
    const buildings = cells.map((c) => ({
        shape: c.shape,
        offset: walk(c.at),
    }));
    return {
        buildings,
        markers: markers.map((m) => walk(m)),
        bridges: bridges.map(({ at }) => {
            if (at.length === 0)
                throw new Error("bridge path needs >= 1 step");
            return [walk(at.slice(0, -1)), walk(at)];
        }),
    };
}
// --- Card factory ---------------------------------------------------------
function card(kind, id, name, obsidian, description, pattern, winTiming) {
    return {
        id,
        kind,
        name,
        description,
        cost: [0, 0, 0, obsidian],
        pattern,
        ...(winTiming ? { winTiming } : {}),
    };
}
const win = (id, name, obsidian, description, pattern, winTiming) => card("win", id, name, obsidian, description, pattern, winTiming);
const regular = (id, name, obsidian, description, pattern) => card("REGULAR", id, name, obsidian, description, pattern);
/** A WIN-deck card that is never dealt — it enters a pile only through
 * another card's effect (the Space Cheese Spy). */
const undealtWin = (id, name, obsidian, description, pattern) => ({
    ...card("win", id, name, obsidian, description, pattern),
    undealt: true,
});
/** A REGULAR card that is never dealt into a deck (the archived Disciple). */
const undealt = (id, name, obsidian, description, pattern) => ({
    ...card("REGULAR", id, name, obsidian, description, pattern),
    undealt: true,
});
/** The Win (victory-condition) deck — the dark cards. */
// Catalogue "50" (2026-10-17 sheet overhaul): every win card re-reads from the
// sheet — thresholds (Giant Sphincter 4, Corrupt Hermit 2, Space Cheese
// Assassin 2 stealing 2, Bankman 15/15/15, Plink 10 production / 15 banked),
// rules (Enchanted Mines on the highest two zones, Vacmak'd −1 on anyone's
// held defence, CG Builder on the highest zone only, Big Sprawler without the
// home-base clause, Parasitic Breakfast on DEFEND range from any building,
// Lone Traveler paying the zone it climbs into with a 1TR move and a
// start-of-turn win, Dog Den 1TR, Curdler's solo round 2 turns) and timings
// (Sky's Tower start-of-turn). BAM Exploder, Stab Pole Hole and Church of
// Penguin are archived; Horny Neighbour and Chicken Dinner are new. Costs are
// as in "49" (the sheet prints none but Horny Neighbour's).
const winCards = [
    win("enchanted-mines", "Enchanted Mines", 17, 
    // The sheet's "W5T-R" — the 5-token win and the round reset — is the tray.
    "Add a token to this card when you build a mine on a resource in the highest 2 energy zones. Remove a token when any of your mines is destroyed.", pat(
    // The middle of the three-in-a-row (the NE cell, between the anchor and
    // the far mine) became a star, and the last bridge dropped (2026-08-21
    // sheet revision).
    [
        b("wild", "NE"),
        b("mine"),
        b("mine", "NE", "SE"),
    ], [
        ["S"],
        ["NE", "SE", "S"],
        ["S", "SE"],
        ["S", "SE", "S"],
    ])),
    win("piercing-needle", "Piercing Needle", 
    // 23 → 28 obsidian (2026-09-26 revision).
    28, // "49": 28
    // The sheet's "All buildings must be in the highest energy zone" is the
    // placement rule, printed in the restriction area from the data.
    "Win game.", 
    // One bridge off the barracks, along the 2-step path toward the wild
    // (the second edge dropped in the 2026-08-20 revision).
    pat([b("barracks"), b("wild", "S", "SE")], [], [br("S")])),
    win(
    // Renamed "Underhand Skyscraper" → "Sky's Tower" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "underhand-skyscraper", "Sky's Tower", 
    // 10 → 11 obsidian (2026-09-26 revision).
    9, // "49": 11
    "Win if you are the only person to occupy the highest energy zone.", pat([
        b("wild", "NW"),
        b("wild", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NW", "N"],
        ["NE", "N"],
        ["S"],
    ], 
    // Just the top span; the two anchor-wild-to-neighbour edges joining the
    // three stars dropped in the 2026-08-20 revision.
    [
        br("NE", "N", "NW", "N"),
    ]), "start-of-turn"),
    win("miner-bots", "Miner Bots", 20, 
    // The sheet's "can only be built if you have at least one mine in every
    // energy zone" is the build gate, printed in the restriction area.
    "Win.", pat([
        b("mine", "NE", "N", "NW"),
        b("wild"),
        b("mine", "S"),
    ], [
        ["NW", "N"],
        ["NE", "N"],
        ["NW"],
        ["NE"],
    ], 
    // The wild (star) to its adjacent S mine.
    [
        br("S"),
    ]), "start-of-turn"),
    win("vacmakd", "Vacmak'd", 11, 
    // "Successfully performs a barracks attack" = a building falls to one;
    // bridge combat and captures fire no building events (engine reading).
    "Place 1 token on this card when anyone's barracks attack destroys a building. Remove 1 token if anyone successfully defends a building from a barracks attack.", pat(
    // The anchor barracks became a star (2026-08-21 sheet revision).
    [
        b("wild"),
        b("barracks", "S"),
        b("wild", "S", "SE", "S", "SW"),
    ], [
        ["NW"],
        ["NE"],
        ["S", "SW"],
        ["S", "SE"],
    ], 
    // The edge between the adjacent star and barracks.
    [
        br("S"),
    ])),
    win("giant-sphincter", "Giant Sphincter", 10, 
    // The sheet's "WT4-R" — the 4-token win and the round reset — is the tray.
    "Place a token on this card for every opponent building that you destroy, by barracks or fortress demolition.", pat(
    // The far mine became a star, and the bridge touching the barracks
    // dropped (2026-08-21 sheet revision).
    [
        b("barracks"),
        b("wild", "S", "SE"),
        b("wild", "S", "SE", "S"),
    ], [
        ["S"],
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SW", "S", "SW"],
    ], 
    // The far edge of the 2-step path between the barracks and the first
    // wild (star) — the barracks-side edge dropped.
    [
        br("S", "SE"),
    ])),
    win("artifact-of-speed", "Artifact of Speed", 3, "Win game if 4 artifacts are built by you. Gain 1 mineral.", pat([b("scout", "S")], [[]], [br("S")])),
    win("artifact-of-courage", "Artifact of Courage", 3, "Win game if 4 artifacts are built by you. Gain 1 food.", pat([b("barracks", "S")], [[]], [br("S")])),
    win("artifact-of-knowledge", "Artifact of Knowledge", 3, "Win game if 4 artifacts are built by you. Gain 1 energy.", pat([b("mine", "S")], [[]], [br("S")])),
    win("artifact-of-plebians", "Artifact of Plebians", 3, "Win game if 4 artifacts are built by you. Gain 1 of any resource.", pat([b("wild"), b("wild", "S")], [["NE"]])),
    win("artifact-of-atrocity", "Artifact of Atrocity", 3, "Win game if 4 artifacts are built by you. Gain 1 obsidian.", pat([], [[]], [br("NW"), br("NE")])),
    win(
    // Renamed "Traveling Booba" → "Lone Traveler" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "traveling-booba", "Lone Traveler", 
    // 5 → 6 obsidian (2026-09-26 revision).
    8, // "49": 6
    // The sheet's "Must be built entirely in the lowest energy zone" is the
    // placement rule below (every zero-penalty zone: forest AND foothill, the
    // reading since "1"); the card face prints it from that data, so the text
    // doesn't repeat it.
    // The sheet's "Destructible" is the badge the face prints from the data.
    "Action: Move this fortress by your barracks attack range. Moving takes energy based on the highest energy zone that you have moved into. Win once completely in the highest energy zone. 1TR", pat([b("scout"), b("scout", "S")]), 
    // Catalogue "18" DROPPED the SOT badge: the win fires the moment the
    // fortress sits entirely in the highest zone — i.e. on the move that puts
    // it there, not on the holder's next turn. No badge = "immediate".
    "start-of-turn"),
    win(
    // Renamed "Bursted Flap" → "Zapped Bapped Statue" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "bursted-flap", "Zapped Bapped Statue", 
    // 12 → 14 obsidian (2026-09-26 revision).
    18, // "49": 14
    "Whenever any player takes an energy-requiring action, you may pay the exact same energy penalty cost onto this card. Win when there are 10 energy resources on this card. Reset the stored energy to 0 at the end of the round.", pat(
    // The anchor scout became a star (2026-08-21 sheet revision).
    [
        b("wild"),
        b("mine", "NW", "SW", "S"),
        b("wild", "S", "SW"),
    ], [
        ["NW", "N"],
        ["NW"],
        ["S"],
        ["S", "SE"],
    ], 
    // A single bridge off the lower wild (star), heading toward the anchor
    // star (the anchor-side and mine-side edges dropped in the 2026-08-20
    // revision).
    [
        br("S", "SW"),
    ])),
    win("obsidian-pipeline-masterworks", "Obsidian Pipeline Masterworks", 
    // 20 → 23 obsidian (2026-09-26 revision).
    18, // "49": 23
    // The sheet's "Can only be built if you have 8 or more fortresses" is the
    // build gate, printed in the restriction area from the data.
    "Win game.", pat(
    // 2026-08-21 sheet revision: the lone far star moved two nodes closer
    // (from 4 edges to 2 edges off the star pair) and became a scout; its
    // stub bridge moved next to the other bridge, so the two bridges now
    // sit side by side spanning the 2-edge path (through the X marker)
    // that connects the two stars.
    [
        b("scout", "S", "SE", "NE"),
        b("wild", "NE", "SE", "NE", "SE", "S"),
        b("wild", "NE", "SE", "NE", "SE", "NE", "SE", "S"),
    ], [
        ["NE", "SE", "NE", "SE", "S", "SE"],
    ], [
        br("NE", "SE", "NE", "SE", "S", "SE"),
        br("NE", "SE", "NE", "SE", "S", "SE", "NE"),
    ])),
    win("parasitic-breakfast", "Parasitic Breakfast", 
    // 13 → 16 obsidian (2026-09-26 revision).
    16, "Win if you have a building within your defend range to 3 fortresses. At least 2 fortresses must be opponent fortresses.", pat(
    // 2026-08-21 sheet revision: the far star and all three X markers each
    // moved one node closer to the anchor/barracks pair, and the bridge
    // moved onto the edge between the anchor star and the barracks.
    [
        b("wild", "NW", "N"),
        b("barracks", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NW", "N", "NW"],
        ["NE", "N", "NW"],
    ], [
        br("NE"),
    ]), "start-of-turn"),
    win("inverse-molehill", "Inverse Molehill", 
    // 5 → 7 obsidian with the catalogue "15" rework: the flat 5-Obsidian
    // discount became one that grows with EVERY fortress implemented in the
    // game — any player's, the holder's own included, from the game's start
    // (implements before this card entered play count too). 7 → 11 with
    // catalogue "25": the growing discount pays for itself too quickly at 7.
    // 11 → 12 obsidian (2026-09-26 revision).
    12, "Win condition fortresses cost X Obsidian less for you, X being the number of fortresses implemented in this game by any player (minimum 1 Obsidian cost).", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NE", "N", "NW"],
        ["NE", "N"],
        ["NE"],
    ], [
        br("NE", "N", "NE"),
        br("NE"),
    ])),
    win("obsidian-blimp-port", "Obsidian Blimp-port", 5, // "49": 3
    // The sheet's "At least 1 scout must be in highest energy zone" is the
    // placement rule (restriction area) and "Destructible" the badge; "W2T-A"
    // is the tray — none of them repeat here.
    "As long as this fortress is intact, add a token to this card at the end of the round.", pat([
        b("scout"),
        b("scout", "NE", "SE", "NE", "SE", "NE", "SE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE", "SE", "NE"],
    ])),
    win("penguin-puncher", "Penguin Puncher", 
    // 13 → 14 obsidian (2026-09-26 revision).
    18, // "49": 14
    "Place a token on this fortress at the end of the round when you are the last player to take an action.", pat(
    // The anchor star — the outlier sitting one node away from the clump
    // of three mutually-adjacent stars — dropped (2026-08-21 sheet
    // revision). The origin no longer holds a building; the offsets keep
    // their original authoring frame.
    [
        b("wild", "S", "SW"),
        b("wild", "S", "SW", "S"),
        b("wild", "S", "SE", "S", "SW"),
    ], [
        ["S"],
        ["S", "SE"],
        ["S", "SE", "S"],
        ["S", "SE", "S", "SW", "S"],
    ], 
    // One centre-ish bridge: the marked-node-to-wild edge in the middle of
    // the cluster (the anchor-side edge dropped in the 2026-08-20
    // revision).
    [
        br("S", "SW"),
    ])),
    win("cg-builder-of-bridges", "CG Builder of Bridges", 
    // 20 → 19 obsidian (2026-08-21 revision, second pass).
    // 19 → 23 obsidian (2026-09-26 revision).
    25, // "49": 23
    "Win when the highest energy zone has all their nodes connected. Nodes must be connected between the highest and second highest energy zone as well.", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW"],
        ["NW"],
        ["NE"],
        ["S"],
    ], 
    // Four bridges (the lone S edge off the wild — pointing away from the
    // rest of the pattern — dropped in the 2026-08-20 revision).
    [
        br("NW", "N"),
        br("NE", "N"),
        br("NW"),
        br("NE"),
    ])),
    win("big-sprawler", "Big Sprawler", 
    // 16 → 18 obsidian (2026-09-26 revision).
    18, "Win if you occupy 72/X tiles, where X equals the number of players there are, rounded up.", pat(
    // 2026-08-21 sheet revision: the scout dropped and the far star moved
    // one node closer, making the pattern fully symmetric — star, 2 nodes,
    // the bridged middle edge, 2 nodes, star — with an X marker adjacent
    // to each star along the line.
    [
        b("wild"),
        b("wild", "NE", "SE", "NE", "SE", "NE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE", "SE"],
    ], [
        br("NE", "SE", "NE"),
    ]), "start-of-turn"),
    win("mirror-morse", "Mirror Morse", 
    // 15 → 17 obsidian (2026-09-26 revision).
    17, "Win if you build a mine on a starting node in the starting tile opposite to your own starting tile.", pat(
    // The centre mine was removed and replaced with an X marker
    // (2026-08-21 sheet revision).
    [
        b("scout", "S"),
        b("wild", "NE", "SE", "NE", "SE", "S"),
    ], [
        ["NE", "SE"],
        ["S", "SE"],
        ["NE", "SE", "S", "SE"],
        ["NE", "SE", "S"],
    ], 
    // The scout-side and wild-side edges of the two spans (the two edges
    // touching the centre dropped in the 2026-08-20 revision).
    [
        br("S", "SE"),
        br("NE", "SE", "S", "SE", "NE"),
    ]), "end-of-round"),
    win("exodial-the-forsaken-one", "Exodial the Forsaken One", 30, // "49": 28
    "Win. May be built using other player's buildings.", pat([
        b("scout", "NE", "N", "NW"),
        b("wild"),
        b("mine", "NW", "SW", "S"),
        b("barracks", "NE", "SE", "S"),
    ], [], [
        br("NW"),
        br("NE"),
        br("S"),
    ])),
    win("bankman", "Bankman", 
    // 22 → 26 obsidian (2026-09-26 revision).
    26, "Win if you have 15 mineral, 15 food and 15 energy in your bank.", pat([
        b("wild"),
        b("mine", "NE", "SE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE"],
        ["S"],
        ["NE", "SE", "S"],
    ], 
    // One bridge: the marked-node-to-mine edge, on the line toward the
    // star (the mine's S stub — the bridge on the mine NOT pointed at the
    // star — dropped in the 2026-08-21 revision).
    [
        br("NE", "SE"),
    ]), "start-of-turn"),
    win("plink-harvester", "Plink Harvester", 
    // 18 → 23 obsidian (2026-09-26 revision).
    23, "Win the game when you have 10 obsidian production and 15 obsidian banked.", pat([
        b("mine"),
        b("wild", "S", "SE"),
        b("wild", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NE", "SE"],
        ["S"],
        ["S", "SE", "S", "SW"],
    ], 
    // The mine-side edge of the 2-edge span through the marked S node (the
    // star-side edge dropped in the 2026-08-21 revision).
    [
        br("S"),
    ]), "end-of-round"),
    win(
    // Renamed "Booba Farmer" → "Sly Sequester" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "booba-farmer", "Sly Sequester", 
    // 15 → 19 obsidian (2026-09-26 revision).
    22, // "49": 19
    "Win if you have more of a resource type banked than the combined total of the same resource type banked by all other players.", pat([
        b("mine", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NW"],
        ["S"],
    ], 
    // The mine's stub and the far edge (the middle bridge sitting between
    // the two others dropped in the 2026-08-20 revision).
    [
        br("NE", "N", "NE", "N"),
        br("NE", "N"),
    ]), "start-of-turn"),
    win("corrupt-hermit", "Corrupt Hermit", 
    // 16 → 7 obsidian (2026-09-08 revision).
    10, // "49": 7
    "You may build fortresses with opponent scouts, barracks and mines. Place a token on this card when you build a fortress where at least one building is controlled by an opponent. Opponent buildings still remain controlled by opponents but cannot be destroyed or recycled.", 
    // Three stars, one bridge: an adjacent pair, and the hermit star sitting
    // apart to the south — its bridge reaching back toward the pair. Three X
    // markers: one on the bridge's free end, and one on either side of it.
    pat([
        b("wild"),
        b("wild", "NE"),
        b("wild", "S", "SE", "S"),
    ], [
        ["S", "SE"],
        ["S"],
        ["S", "SE", "NE"],
    ], [
        br("S", "SE", "S"),
    ])),
    win("dog-den", "Dog Den", 
    // 5 → 8 obsidian (2026-08-21 revision, third pass).
    8, "Action: Mark 1 win and 1 regular fortress. You may then shuffle any number of them back into the deck. Win once 1 marked win and 1 marked regular fortress is implemented. 1TR", 
    // A lone star with bridges off two of its edges — the cheapest den there
    // is. The second bridge (2026-08-21 revision, fourth pass) keeps the
    // footprint distinct from Poopy Investigator's star-plus-one-bridge; no
    // other card is a single building with exactly two bridges.
    pat([
        b("wild"),
    ], [], [
        br("S"),
        br("NW"),
    ])),
    win("curdlecat", "CurdleCat", 12, // "49": 15
    "Action: Copy the effect of any implemented fortress, including your own. Only one copied effect at a time. If a win condition is met for both the original and the copied fortress at the exact same time, the original fortress takes priority. 2T", 
    // Two stars three nodes apart, a bridge off each: one on the anchor
    // star's S edge, one reaching the far star. Four X markers flank the
    // span — one beside each star, two along it.
    pat([
        b("wild"),
        b("wild", "S", "SE", "S"),
    ], [
        ["NE"],
        ["S", "SW"],
        ["S", "SE", "NE"],
        ["S", "SE", "S", "SE"],
    ], [
        br("S"),
        br("S", "SE", "S"),
    ])),
    win("singularity-ball", "Singularity Ball", 
    // 15 → 17 obsidian (2026-09-26 revision).
    19, // "49": 17
    // Catalogue "15" rework: the bank threshold became a food-absorption
    // pile — every Food the HOLDER spends lands on the Ball.
    "Whenever you spend Food, place it on this fortress. Win at 30 stored food.", 
    // A ball: the six corners of one hex. The mine and the star face each
    // other across the ring, the four X markers fill the remaining corners,
    // and the two ring edges flanking the star are bridged.
    pat([
        b("mine"),
        b("wild", "NE", "SE", "S"),
    ], [
        ["NE"],
        ["NE", "SE"],
        ["NE", "SE", "S", "SW"],
        ["NE", "SE", "S", "SW", "NW"],
    ], [
        br("NE", "SE", "S"),
        br("NE", "SE", "S", "SW"),
    ])),
    win("space-cheese-curdler", "Space Cheese Curdler", 10, "If another player meets a win condition while this fortress is intact, postpone their victory: play one whole round on your own, from round start to round finish, taking 2 turns in it. Their win condition executes when that round ends, unless you meet your own win condition first.", 
    // The curdler: a star core with a building on three of its edges, ringed
    // by six X markers — the full second ring around the anchor.
    pat([
        b("wild"),
        b("scout", "NE"),
        b("mine", "S"),
        b("barracks", "NW"),
    ], [
        ["NE", "N"],
        ["NE", "SE"],
        ["S", "SE"],
        ["S", "SW"],
        ["NW", "N"],
        ["NW", "SW"],
    ])),
    win("space-cheese-assassin", "Space Cheese Assassin", 13, "Immediately shuffle a Space Cheese Spy into the win deck. When another player cycles the Spy into the market, add a token to this fortress and steal 2 resources from them, their pick. The Spy then goes to the discard pile.", 
    // The assassin: a scout at the point with the wedge of cheese (a star) and
    // the barracks two steps out either side of it, bridged on the way down to
    // the wedge, three holes punched around the arms. Three buildings, one
    // bridge and three X's — a wider footprint than Sky's Tower, short of
    // CurdleCat's two-bridge span, and a building triangle no other card cuts.
    pat([
        b("scout"),
        b("wild", "S", "SE"),
        b("barracks", "NW", "SW"),
    ], [
        ["NE"],
        ["S", "SW"],
        ["NW", "N"],
    ], [
        br("S"),
    ])),
    // --- 2026-10-17 sheet (catalogue "50"): two new win cards. The sheet gives
    // their effects only; the patterns below are original (no card art yet) —
    // three pieces each, checked distinct from every other card under all 12
    // orientations.
    win("horny-neighbour", "Horny Neighbour", 
    // The sheet prices it "50/X": the printed 50 is a TOTAL the engine splits
    // across the seated players, rounded up (`costSplitByPlayers`).
    50, "Win if you have buildings/fortresses within your scout building range to all opponent home bases.", 
    // Two scouts two steps apart peering over the fence: the far scout's
    // bridge reaches back toward the anchor, an X on the node between them and
    // one on either free side of the anchor.
    pat([b("scout"), b("scout", "S", "SE")], [
        ["S"],
        ["NE"],
        ["NW"],
    ], [br("S", "SE")])),
    win("chicken-dinner", "Chicken Dinner", 
    // Priced against Miner Bots (a presence in every zone, win on implement).
    21, // "49": 20
    "Win if you have a fortress fully contained in every energy zone.", 
    // The dinner plate: a mine at the centre with a scout and a barracks on
    // two of its three paths, the X on the third.
    pat([b("mine"), b("scout", "NE"), b("barracks", "NW")], [["S"]]), "end-of-round"),
];
/**
 * Win-deck cards that exist in the catalogue but are dealt into NO deck
 * (`undealt`): they enter a pile only through another card's effect, and ride
 * in `winCards` so they travel inside the same catalogue document (and land in
 * the room's card lookup) as everything else.
 */
const undealtWinCards = [
    // Space Cheese Assassin's decoy (catalogue "50"; "15"–"49" shuffled a copy
    // of the Assassin itself). Never implementable: `decoyFor` makes every draw
    // of it burn — the Assassin's owner takes their token and theft when
    // someone else turned it up — and it lands in the win discard pile.
    undealtWin("space-cheese-spy", "Space Cheese Spy", 0, "A Space Cheese Assassin's decoy. It is never built: when another player cycles it into the market, that Assassin's owner adds a token to their fortress and steals 2 resources from them, their pick. The Spy then goes to the discard pile.", 
    // A lone X: the spy's calling card. Decorative — the card is never matched.
    pat([], [[]])),
];
/**
 * ARCHIVED undealt regular card (catalogue "50"): the Disciple was only ever
 * reachable through the Church of Penguin's `alwaysBuildable` grant, and left
 * the catalogue with it. Kept here, effects attached, for matches pinned to
 * the versions that shipped it.
 */
const archivedUndealtCards = [
    undealt("disciple", "Disciple", 0, "A Disciple of the Church of Penguin. Free to build, but you pass for the rest of the round the moment it rises. It converts every opponent fortress inside your Barracks defend range at the end of the round, then self-destructs — and unlike other fortresses, it can be destroyed.", 
    // One Barracks with all three of its paths bridged: the missionary at the
    // crossroads. Only an interior node has three paths, so a Disciple can
    // never be raised on the board's rim.
    pat([b("barracks")], [], [br("NE"), br("NW"), br("S")])),
];
/** The Regular (effect) deck — the light cards. */
// Catalogue "49" repricing (2026-10-16): every regular card is 1 obsidian
// cheaper than its "48" price (floored at 1), except Times Up, Tokenizer 9000
// and Conversion Treadmill at 6 and Clobbering Bat held at 5. Per-card
// comments above older costs record earlier revisions as they were.
// Catalogue "51" (2026-10-17, later the same day): Poopy Investigator 2 → 3,
// Clobbering Bat 5 → 4 and its Active is 1T (one use per game, no round
// reset) instead of 1TR.
// Catalogue "52" (2026-10-18): Bridgette the Beauty's Active ("Build a bridge
// with +4 building range and no action cost. 1TR") gains its grant
// (`free_bridge`); text and cost unchanged.
// Catalogue "53" (2026-10-18, later the same day): Big Boom's Active becomes
// "Add or remove up to 3 tokens from your regular fortresses. 1T" (no token
// reservoir; one use counted by its own token); Dungeon Dice Martyr's
// "Implement a fortress in the discard pile. 2T" gains its grant
// (`implement_from_discard`).
// Catalogue "54" (2026-10-20 sheet, folded into the unshipped 2026-10-19
// "54"): the sheet prints each card's TOTAL obsidian, which counts the
// Bridges in its pattern — so `cost` is that total minus the pattern's
// bridge count (the per-card comments show the sum). Repriced from "53":
// Godspeed Machine 2 → 4, Bridgette the Beauty 1 → 0, Hole Worm 1 → 2,
// Resource Sequencer 2 → 3, Conversion Treadmill 6 → 5, Sudoku Bear 3 → 5,
// Swole Turtle 1 → 3, Kinetic Absorption Facility 3 → 5, Transmutation Bench
// 2 → 1, Telescopic Arm 2 → 3, Hurtful Shrine 1 → 3, Dungeon Dice Martyr
// 1 → 5, Cyclopz 3 → 5, Curious Climb 2 → 4, Action Blaster 1 → 2, Times Up
// 5 → 2, Trifecta Sphinx 1 → 3, Rabid Shoes 2 → 4, Recyclomaniac 2 → 3,
// Hippoman 2 → 3, Morphing Midget 1 → 2, Gut Re-arranger 2 → 5, Apex Butt
// Blast 1 → 3, Grand Entrance 2 → 4. Rules: Hurtful Shrine's Barracks build
// reaches +2 range (was +4); Rabid Shoes' scout/bridge range +5 → +4;
// Clobbering Bat 1T → 2T; Grand Entrance 2T → 3T; Porkypine Neck's Active
// may untap a Barracks again; Poopy Investigator searches the regular deck
// only; Dungeon Dice Martyr's Active shuffles a discarded fortress back into
// its deck and then cycles up to 2 cards of that row for free (1TR), and its
// passive is +2 banked obsidian or +1 obsidian production. Cyclopz ("from
// one row") and Resource Sequencer change wording only. Every token
// add/remove stays on REGULAR-deck fortresses (Wei, 2026-10-20 — the sheet's
// Tokenizer 9000 / Big Boom lines dropped the word, the rule did not).
// Text-vs-behaviour fixes in the same pass: Telescopic Arm's capture pays 2
// (was 1); Times Up's end-of-round priority is a real passive (was only the
// marker its Active hands over); Supreme Bondage's "add/remove a token"
// rider is modelled (regular only); Transmutation Bench loses its unprinted
// lowest-zone gate; Curious Climb can't route glanced cards to the discard.
// Catalogue "50" (2026-10-17): Godspeed Machine, Poopy Investigator and
// Energized Bun-Bun 1 → 2, Sudoku Bear 2 → 3, Times Up and To the moon
// Tokenizer 9000 6 → 5, Grand Entrance 3 → 2 (and 3T → 2T), Big Boom 2 → 3
// (reservoir 5 → 3 tokens); Miss Mineralverse Maggot renamed Miss
// Mineralverse (same id); Nomadic Pufferfish's passive gains +1 attack &
// defend range.
const regularCards = [
    regular("godspeed-machine", "Godspeed Machine", 3, // "56": 4 total ("54": 5 total − 1 bridge = 4; "53": 2, "49": 1)
    "Action: Make one of your destructible buildings indestructible. 2T\nPassive: Gain +2 banked minerals, food or energy. Or gain +1 mineral, food or energy production.", pat([
        b("scout", "S"),
        // Two nodes from the scout (was four; 2026-08-21 sheet revision).
        b("barracks", "S", "SE", "S"),
    ], [
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SE", "S", "SW"],
        // The barracks' old spot, four nodes from the scout (2026-08-21).
        ["S", "SE", "S", "SW", "S"],
    ], 
    // Off the scout (circle), toward its adjacent X marker (2026-08 sheet
    // revision).
    [
        br("S", "SW"),
    ])),
    regular("bridgette-the-beauty", "Bridgette the Beauty", 
    // 1 → 2 obsidian in catalogue "33", paying for the free-crossing line the
    // same revision added. ("8" had already cut her from 2 to 1.)
    // "54": 2 total − 2 bridges = free; the two Bridges are her whole price
    // ("53": 1). "55": 1 (3 total).
    1, "Action: Build a bridge with +4 building range and no action cost. 1TR\nPassive: Build bridges using any resource. Building/attacking/defending over bridges does not take any range.", pat([
        b("mine", "S"),
    ], [
        ["NW", "SW", "S"],
        ["S", "SW"],
        ["S", "SE"],
    ], [
        br("S", "SW"),
        br("S", "SE"),
    ])),
    regular("hole-worm", "Hole Worm", 2, // "54": 5 total − 3 bridges ("53": 1)
    "Action: Place a copy of this fortress anywhere in the same energy level as where it was initially implemented. You cannot destroy buildings with this action. 1TR.\nPassive: Gain +3 minerals. Or gain +1 mineral production.", 
    // The X marker sits at the anchor — the centre node all three bridges
    // share (an empty path walks nowhere).
    pat([], [[]], [br("NW"), br("NE"), br("S")])),
    regular("skippity-star", "Skippity Star", 4, "Action: Double all banked resources. 1T.\nPassive: Gain +2 banked resources.", pat([
        b("mine"),
        // One of the two mines became a star (2026-08-21 sheet revision).
        b("wild", "NE", "SE"),
    ], [
        ["NE", "N"],
        ["NE"],
        ["S"],
        ["NE", "SE", "S"],
    ], 
    // Both edges of the 2-step path connecting the mine to the star,
    // through the marked NE node (2026-08 sheet revision).
    [
        br("NE"),
        br("NE", "SE"),
    ])),
    regular("clobbering-bat", "Clobbering Bat", 
    // 4 → 5 obsidian (2026-09-08 revision, catalogue "32"); back to 4 in
    // "51" (2026-10-17), where the Active also becomes a once-per-GAME use
    // ("1TR" → "1T": the token no longer clears at the end of the round).
    // "54": two uses per game (2T), still 4 (5 total − 1 bridge).
    4, "Action: Add/remove a token from a regular fortress you own. 2T\nPassive: Gain 1 obsidian per implemented fortress that you own at the start of the round.", pat([
        b("mine"),
        b("wild", "S", "SW"),
    ], [
        ["NW", "SW", "NW"],
        ["NW", "SW"],
        ["NW", "SW", "S"],
    ], 
    // One bridge between the two marked middle nodes — the bridges nearest
    // to and farthest from the star dropped (2026-08-21 sheet revision).
    [
        br("S", "SW", "NW", "N"),
    ])),
    regular("resource-sequencer", "Resource Sequencer", 3, // "54": 4 total − 1 bridge ("53": 2)
    "Action: Build a mine without action or building costs. Energy penalties still apply. 1TR\nPassive: Gain +2 banked energy. Or gain +1 energy production.", pat([
        b("wild", "NW"),
        b("wild", "NE", "SE", "S"),
    ], [
        ["NW", "N"],
        ["NE"],
        ["NW", "SW"],
        ["S"],
    ], 
    // The mine at the origin dropped; a bridge now joins the NW star to its
    // old spot (2026-08-21 sheet revision).
    [
        br("NW"),
    ])),
    regular("energized-bun-bun", "Energized Bun-Bun", 2, // "49": 1
    "Action: Build a building that collects twice as many one-time resources. 4T\nPassive: Gain +2 scout range.", pat([
        b("wild"),
        b("wild", "NW", "SW", "S"),
    ], [
        ["NW"],
        ["NE"],
        ["S"],
        ["NW", "SW", "S", "SW"],
    ], 
    // One star-adjacent edge of the 3-step path between the stars (the
    // other side's bridge dropped, 2026-08-21 sheet revision).
    [
        br("NW"),
    ])),
    regular("conversion-treadmill", "Conversion Treadmill", 5, // "54": 6 total − 1 bridge ("53": 6)
    "Action: Lock/unlock a fortress card. This does not consume an action. 1TR\nPassive: Your obsidian may be used as any resource.", pat([
        b("mine", "NW"),
        b("wild"),
    ], [
        ["NW", "N", "NW"],
        ["NW", "N"],
        ["NW", "SW", "NW"],
        ["NW", "SW"],
    ], 
    // The edge between the adjacent wild (star) and mine (2026-08 sheet
    // revision).
    [
        br("NW"),
    ])),
    regular("sudoku-bear", "Sudoku Bear", 5, // "54": 7 total − 2 bridges ("53": 3, "49": 2)
    "Action: Gain resources equal to your production. 1T\nPassive: Gain +1 of any production.", pat([
        b("barracks"),
        // The scout (circle) at S,SE dropped (2026-08-21 sheet revision).
        b("wild", "S", "SE", "S"),
    ], [
        ["NW"],
        ["S"],
        ["S", "SE", "S", "SE"],
    ], 
    // The center bridge of the three-bridge span (marked S node to the
    // scout's old spot) dropped with the scout (2026-08-21 sheet revision).
    [
        br("S", "SE", "S"),
        br("S"),
    ])),
    regular("plasmamagnetic-waves", "Plasmamagnetic Waves", 4, "Action: Gain +1 energy. 1TR\nPassive: Energy penalties reduced from 1/3/5 -> 1/2/3.", pat(
    // Both stars sit one node closer to the scout (circle) than on the
    // earlier sheet (2026-08-21 revision).
    [
        b("wild", "S"),
        b("scout", "S", "SW", "S"),
        b("wild", "S", "SW", "S", "SW", "S"),
    ], [
        ["NE"],
        ["NE", "SE"],
        ["S", "SW", "S", "SW", "S", "SW", "S"],
        ["S", "SE", "S", "SW", "S", "SW", "S", "SW"],
    ])),
    regular("swole-turtle", "Swole Turtle", 3, // "54": 4 total − 1 bridge ("53": 1)
    "Action: Gain 2x the amount of energy stored on this card. You may not deposit energy this round. 1TR.\nPassive: Gain +1 building range. You may deposit any amount of banked energy onto this card at the end of the round.", pat([
        b("wild", "S"),
        b("barracks", "S", "SE", "S"),
    ], [
        ["S", "SE"],
        ["NE", "SE", "S", "SE", "S"],
        ["S", "SE", "S", "SE"],
    ], [
        br("S"),
    ])),
    regular("kinetic-absorption-facility", "Kinetic Absorption Facility", 5, // "54": 5 total, no bridges ("53": 3)
    "Action: Build a building without any energy penalties. 4T\nPassive: Gain +2 banked energy. Or gain +1 energy production.", pat([
        b("wild"),
        b("barracks", "S"),
        // One node closer than on the earlier sheet (2026-08-21 revision).
        b("wild", "S", "SE", "S", "SW"),
    ], [
        ["NW"],
        ["NE"],
        ["NW", "SW"],
        ["NE", "SE"],
    ])),
    regular("transmutation-bench", "Transmutation Bench", 2, // "56": 4 total ("54": 3 total − 2 bridges = 1; "53": 2)
    "Action: Untap 1 of your barracks. Or perform an action without consuming an action. 1TR\nPassive: Your food and energy are interchangeable.", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW"],
        ["NW", "N"],
        ["NE", "SE", "S"],
        ["S", "SE"],
    ], [
        br("NW", "SW"),
        br("S", "SW"),
    ])),
    regular("telescopic-arm", "Telescopic Arm", 3, // "54": 3 total, no bridges ("53": 2)
    "Action: Untap 1 of your barracks. Or perform an action without consuming an action. 1TR\nPassive: Gain +2 attack & defend range. After successfully destroying an opponent building, you may give them 2 of any resource to take over their building.", pat([
        // The outer triangle became a star (2026-08-21 sheet revision).
        b("wild", "NW"),
        b("barracks"),
        b("wild", "NE", "SE", "S"),
    ], [
        ["NW", "N", "NW"],
        ["NW", "N"],
        ["NW", "SW", "NW"],
        ["NE"],
        ["NW", "SW"],
        ["S"],
    ])),
    regular("porkypine-neck", "Porkypine Neck", 1, // "54": 2 total − 1 bridge
    // "54": the untap option is back ("49"-"53": the free action only).
    "Action: Untap 1 of your barracks. Or perform an action without consuming an action. 1TR\nPassive: Gain +2 attack & defend range. You may attack & defend through buildings, fortresses and bridge gaps.", pat([
        b("barracks", "NE", "N", "NW"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NW", "N", "NW"],
        ["NE", "N", "NE"],
        ["NW", "N"],
        ["NE", "N"],
    ], [
        br("NE", "N", "NW", "N"),
    ])),
    regular(
    // Renamed "iCarlie" → "Hurtful Shrine" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "icarlie", "Hurtful Shrine", 3, // "54": 4 total − 1 bridge ("53": 1)
    // "54": the Barracks reaches +2 range ("49"-"53" printed +4).
    "Action: Build a barracks with +2 range and no energy penalties. 1TR\nPassive: If a player builds within your defence range to this fortress, gain +2 banked resource of any kind.", pat(
    // The wild (star) at NW,SW,S dropped; a bridge now joins the farthest
    // X marker to the star's old spot (2026-08-21 sheet revision — no X
    // marker is added where the star stood).
    [
        b("barracks"),
    ], [
        ["NW"],
        ["NE"],
        ["S"],
        ["NW", "SW", "S", "SW"],
    ], [
        br("NW", "SW", "S", "SW"),
    ])),
    regular("dungeon-dice-martyr", "Dungeon Dice Martyr", 5, // "54": 6 total − 1 bridge ("53": 1)
    // "54": a new Active and passive ("53": "Implement a fortress in the
    // discard pile. 2T" / "Gain +1 obsidian production").
    "Action: Shuffle any fortress from the discard pile back into its corresponding deck. Then, cycle up to 2 cards from that row for free. 1TR\nPassive: Gain +2 banked obsidian or +1 obsidian production.", pat(
    // The mine and the star either side of the bridge dropped; only the
    // star's old spot (the origin) gains an X marker (2026-08-21 sheet
    // revision).
    [
        b("wild", "NE", "SE", "NE"),
        b("wild", "NE", "SE", "NE", "SE"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NE", "SE"],
        [],
    ], 
    // The edge between the star's old spot (now the marked origin) and the
    // mine's old spot (2026-08 sheet revision).
    [
        br("NE"),
    ])),
    regular("supreme-bondage", "Supreme Bondage", 1, 
    // "54": "a REGULAR fortress you own" — every token add/remove is
    // regular-only (Wei, 2026-10-20; the sheet prints "a fortress you own").
    "Action: Lock/unlock a fortress card. This does not consume an action. 2TR\nPassive: Whenever any player implements a locked fortress, gain 2 of any resource and add/remove a token from a regular fortress you own.", pat([
        b("scout", "S"),
        b("wild", "S", "SE", "S", "SW", "S"),
    ], [
        ["NW", "SW", "S"],
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SE", "S", "SW"],
    ], [
        br("S", "SE", "S", "SW", "S"),
    ])),
    regular("cyclopz", "Cyclopz", 5, // "54": 5 total, no bridges ("53": 3)
    "Action: Cycle as many cards as you want from one row without any cost. 3T.\nPassive: Gain +1 action per turn.", pat(
    // The barracks (triangle) and every X marker moved one node closer to
    // the stars (2026-08-21 sheet revision).
    [
        b("wild"),
        b("wild", "NE", "SE"),
        b("barracks", "S", "SE"),
    ], [
        ["S", "SW"],
        ["NE", "SE", "S", "SE"],
        ["S", "SE", "S"],
        ["NE", "SE", "S", "SE", "S"],
    ])),
    regular(
    // Renamed "Curious Clit" → "Curious Climb" on the sheet; the id is
    // load-bearing (effects registry, saved games) and stays stable.
    "curious-clit", "Curious Climb", 4, // "54": 4 total, no bridges ("53": 2)
    "Action: Look at the top 5 fortresses of any deck. You may re-arrange them or move any of them to the bottom of the pile. Then, cycle up to 2 fortresses from the current deck for free without any action cost. 1TR.\nPassive: Gain +1 building range.", pat([
        b("wild", "NW"),
        b("wild"),
        b("barracks", "NW", "SW", "S"),
    ], [
        ["S"],
        ["S", "SW"],
        ["S", "SE"],
        ["S", "SE", "S"],
    ])),
    regular("action-blaster", "Action Blaster", 2, // "54": 3 total − 1 bridge ("53": 1)
    "Action: Untap 1 of your barracks. Or perform an action without consuming an action. 1TR\nPassive: Gain 1 extra action per turn.", pat([
        b("wild", "NW", "N", "NW"),
        b("wild"),
    ], [
        ["NW", "N", "NW", "N", "NW"],
        ["NW", "N", "NW", "N"],
        ["NE"],
        ["NE", "SE"],
    ], [
        br("NW", "N"),
    ])),
    regular("times-up", "Times Up", 3, // "56": 4 total ("54": 3 total − 1 bridge = 2; "53": 5, "49": 6)
    "Action: End the current round and take the first turn marker. 1T\nPassive: Your end of round win fortresses take priority over other end of round win fortresses.", pat([
        b("wild", "S"),
        b("wild", "S", "SW", "S"),
        b("wild", "S", "SE", "S"),
    ], [
        [],
        ["S", "SW"],
        ["S", "SE"],
        ["S", "SE", "S", "SW", "S"],
    ], [
        br("S", "SE", "S", "SW", "S"),
    ])),
    regular("to-the-moon-tokenizer-9000", "To the moon Tokenizer 9000", 5, // "54": 6 total − 1 bridge ("49": 6)
    "Action: Add/remove a token from a regular fortress you own. 1TR\nPassive: Actions that add/remove tokens from fortresses no longer cost actions for you.", pat(
    // The lone star and the two X markers farthest from the remaining pair
    // dropped (2026-08-21 sheet revision).
    [
        b("wild", "NE"),
        b("wild"),
        // The node between the anchor star and the X marker (2026-08-21 sheet
        // revision).
        b("wild", "S"),
    ], [
        ["S", "SE"],
    ], 
    // The edge between the two adjacent wilds (stars) (2026-08 sheet
    // revision).
    [
        br("NE"),
    ])),
    regular("trifecta-sphinx", "Trifecta Sphinx", 3, // "54": 3 total, no bridges ("53": 1)
    "Action: Untap 1 of your barracks. Or perform an action without consuming an action. 1TR\nPassive: Gain +3 building range and +1 attack & defend range.", 
    // The bridge dropped (2026-08-21 sheet revision).
    pat([
        b("mine", "NE", "N", "NW"),
        b("wild"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NE"],
    ])),
    regular("rabid-shoes", "Rabid Shoes", 4, // "54": 4 total, no bridges ("53": 2)
    // "54": +4 scout/bridge range ("49"-"53": +5).
    "Action: Build any building with +4 building range. 1TR\nPassive: Gain +4 building range for your scouts and bridges.", pat([
        b("scout"),
        b("wild", "S", "SE"),
    ], [
        ["S"],
        ["NE", "SE", "S"],
        ["NE", "SE", "S", "SE"],
    ])),
    regular("recyclomaniac", "Recyclomaniac", 3, // "54": 4 total − 1 bridge ("53": 2)
    "Action: Sell one of your buildings for 100% of its build cost. Energy is not refunded, and one-time resources cannot be re-collected. 1TR\nPassive: Gain 1 additional action per turn.", 
    // Mine and star adjacent, three X markers around them; the bridge runs
    // from the mine to its adjacent X marker (not to the star).
    pat([
        b("mine"),
        b("wild", "NE"),
    ], [
        ["S"],
        ["NE", "SE"],
        ["NE", "N"],
    ], [
        br("S"),
    ])),
    regular("poopy-investigator", "Poopy Investigator", 3, // "54": 4 total − 1 bridge ("50": 2, "49": 1)
    // Catalogue "41" rework: the found card no longer swaps into the row
    // locked — it goes on top of the (reshuffled) deck, and only the searcher
    // learns which card it was. "54": the regular deck only ("41"-"53":
    // either deck).
    "Action: Search the regular fortress decks for any card you want. Only you see this card. Shuffle the deck, and then place the card you want on top. 1T\nPassive: Gain +1 banked obsidian.", 
    // A star flanked by two X markers; the bridge faces the star's one
    // neighbouring node that is NOT an X marker.
    pat([
        b("wild", "S"),
    ], [
        ["S", "SW"],
        ["S", "SE"],
    ], [
        br("S"),
    ])),
    // --- 2026-08-22 sheet revision: five new regular cards --------------------
    // Patterns are original (no PDF art yet): each uses 2-3 building/bridge
    // pieces with at least one wild star and 2-6 X markers, and each buildable
    // footprint (buildings + bridges, X markers aside, wilds matching any
    // shape) is distinct from every other card's up to translation, the 6
    // rotations, and mirroring.
    regular("nomadic-pufferfish", "Nomadic Pufferfish", 2, // "54": 3 total − 1 bridge
    "Action: Move one of your non-fortress buildings to an empty node within your mine's building range of it. No energy penalties. The destination does not need to be in range of another one of your buildings. 1TR\nPassive: Gain +1 mine building range and +1 attack & defend range.", 
    // A star and a mine two steps apart; the bridge hangs off the star's far
    // side, away from the mine. Three X markers fan around the star.
    pat([
        b("wild"),
        b("mine", "NE", "SE"),
    ], [
        ["NW"],
        ["NE", "N"],
        ["S", "SW"],
    ], [
        br("S"),
    ])),
    regular("hippoman", "Hippoman", 3, // "54": 4 total − 1 bridge ("53": 2)
    "Action: Lock/unlock a fortress card. This does not consume an action. 1TR\nPassive: Food-consuming actions cost 1 less (minimum 1).", 
    // A star and a barracks two steps apart; the bridge floats off-pattern,
    // one step out from the star (touching neither building) — like
    // Clobbering Bat's marker-to-marker span. Tightened on the 2026-08-22
    // pass: every X pulled to within two steps of the star (the old spray
    // reached three), buildings and bridge unchanged.
    pat([
        b("wild"),
        b("barracks", "S", "SE"),
    ], [
        ["NW"],
        ["NE"],
        ["S", "SW"],
        ["NE", "SE"],
    ], [
        br("NE", "N"),
    ])),
    regular("morphing-midget", "Morphing Midget", 2, // "54": 3 total − 1 bridge ("53": 1)
    "Action: Gain +2 of any single resource. 1TR\nPassive: Gain +1 building range.", 
    // A star and a mine three bent steps apart, the bridge on the star's NE
    // edge and two X markers trailing off the far side of the mine.
    pat([
        b("wild"),
        b("mine", "S", "SW", "S"),
    ], [
        ["S", "SW", "S", "SW"],
        ["S", "SW", "S", "SE"],
    ], [
        br("NE"),
    ])),
    regular(
    // Renamed "Demented Rapist" → "Gut Re-arranger" on the 2026-10-16 sheet;
    // the id is load-bearing (effects registry, saved games) and stays stable.
    "demented-rapist", "Gut Re-arranger", 5, // "54": 5 total, no bridges ("53": 2)
    "Action: Redistribute your resources however you want to. 1T\nPassive: Gain 1 mineral every time you build on an empty node.", 
    // Original pattern (no PDF art yet), authored to the 2026-08-22 rules: a
    // star hub with two arms of UNEVEN length — a scout two steps out one way,
    // a mine three steps out the other — the card's "everything through one
    // pile, back out unevenly" read. The uneven arms are what keep the
    // footprint (buildings + bridges, wilds as jokers) off Cyclopz's, which
    // hangs its three buildings at two steps either side of the hub; distinct
    // from every other card's too, up to translation, rotation and mirroring.
    // One X marker sits on each arm, one trails past the scout, and one takes
    // the hub's third edge.
    pat([
        b("wild"),
        b("scout", "NE", "SE"),
        b("mine", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NW"],
        ["S", "SE"],
        ["NE", "SE", "NE"],
    ])),
    // --- 2026-10-16 sheet (catalogue "49"): four new regular cards. The sheet
    // gives their effects only; the patterns below are original (no card art
    // yet) — three pieces each, checked distinct from every other card under
    // all 12 orientations — and the costs are set against comparable cards.
    regular("apex-butt-blast", "Apex Butt Blast", 3, // "54": 4 total − 1 bridge ("53": 1)
    "Action: Build a scout with +3 range. Do not pay building or energy costs. 2T\nPassive: Gain +2 banked minerals, food or energy. Or +1 mineral, food or energy production.", 
    // Two scouts two bent steps apart, with a bridge off the anchor's far
    // side. X markers: the node between the scouts, the bridge's far end, and
    // the anchor's third neighbour.
    pat([
        b("scout"),
        b("scout", "NE", "SE"),
    ], [
        ["NE"],
        ["S"],
        ["NW"],
    ], [br("S")])),
    regular("miss-mineralverse-maggot", "Miss Mineralverse", // "49": Miss Mineralverse Maggot (same id)
    2, "Action: All other players get +1 mineral. You get +3 minerals. 1TR\nPassive: Every time you build on a one-time resource, you may cycle/lock/unlock a card", 
    // Three mines in a row, two steps apart, the anchor in the middle; X
    // markers on the two nodes between them and on the anchor's free side.
    pat([
        b("mine"),
        b("mine", "NE", "SE"),
        b("mine", "NW", "SW"),
    ], [
        ["NE"],
        ["NW"],
        ["S"],
    ])),
    regular("big-boom", "Big Boom", 3, // "54": 5 total − 2 bridges ("49": 2)
    // "53": up to 3 token changes in one use, no reservoir on the card
    // ("50"-"52" re-distributed a 3-token reservoir; "49" printed 5).
    "Action: Add or remove up to 3 tokens from your regular fortresses. 1T\nPassive: Double or halve the token capacity of another regular fortress you own.", 
    // A barracks with bridges on two of its three edges; X markers at both
    // bridges' far ends and on the third neighbour.
    pat([b("barracks")], [
        ["NE"],
        ["NW"],
        ["S"],
    ], [br("NE"), br("NW")])),
    regular("grand-entrance", "Grand Entrance", 4, // "54": 4 total, no bridges ("53": 2, "49": 3)
    // "50": two uses per game ("49" printed 3T); "54": three again; "56": two.
    "Action: Re-enter the round if you already passed. You get +3 actions on the turn that you re-enter. 2T\nPassive: Gain +2 banked obsidian. Or gain +1 obsidian production", 
    // Scout, barracks, mine in a bent line — one of each — with X markers
    // flanking it: beside the scout, beside the barracks, and past the mine.
    pat([
        b("scout"),
        b("barracks", "S"),
        b("mine", "S", "SE"),
    ], [
        ["NE"],
        ["S", "SW"],
        ["S", "SE", "NE"],
    ])),
];
// --- Archived cards -------------------------------------------------------
//
// Cards taken off the sheet. They are NOT dealt into any NEW catalogue
// version, but every version that shipped them keeps its own snapshot in Mongo
// — matches pinned to those versions still deal, implement and resolve them,
// so their effects (and the engine code behind them) stay live and their
// entries stay in `CARD_EFFECTS_CURRENT` below. Un-archiving is a matter of
// moving the definition back into the deck above and publishing a new version.
const archivedRegularCards = [
    regular("frienemy", "Frienemy", 3, "Gain +1 resource of any kind for each opponent building within your mine range to one of your buildings at the start of the round. Max 1 resource per opponent building, no matter how many of your buildings are beside it.", 
    // A star and a scout four steps apart in a straight line — neighbours at
    // the ends of a street of X markers (two on the line, one either side).
    pat([
        b("wild"),
        b("scout", "NE", "SE", "NE", "SE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE"],
        ["NW"],
        ["S"],
    ])),
    regular("obsidian-ovary-overload", "Obsidian Ovary Overload", 5, "You may build fortresses with one less building or bridge piece. You decide which piece is removed from the configuration.", 
    // Mine and star two steps apart, with the four X markers hugging the
    // pair. The bridge sits on the star's far side, facing the X marker away
    // from the mine.
    pat([
        b("mine"),
        b("wild", "S", "SE"),
    ], [
        ["S"],
        ["NE"],
        ["NW"],
        ["S", "SE", "S"],
    ], [
        br("S", "SE", "S"),
    ])),
    // --- ARCHIVED in catalogue "49" (2026-10-16 overhaul): every regular card
    // now prints an Active and a Passive effect, and these four are off the new
    // sheet. Matches pinned to "48" and earlier still play them. -----------
    regular("shifty-engineer", "Shifty Engineer", 2, "After building this fortress, select either: scouts no longer cost actions, or bridges no longer cost actions, for the rest of the game. Place a scout or bridge on this card to denote which one was chosen.", 
    // Same pattern as Bridgette the Beauty, with a scout in the mine's place.
    pat([
        b("scout", "S"),
    ], [
        ["NW", "SW", "S"],
        ["S", "SW"],
        ["S", "SE"],
    ], [
        br("S", "SW"),
        br("S", "SE"),
    ])),
    regular("block-us-baby", "Block Us Baby", 5, "Gain a new action: place any building for free, without requiring resources, vigor penalties, or actions. Use your barracks defend range as the building range. Place a token on this card when you do so. You cannot exceed 2 tokens.", 
    // A scout caps a zig-zag line of five X markers; the bridge spans the
    // line's second edge, joining the first X to a star on the bridge's far
    // node (that X became the star, 2026-08-21 sheet revision).
    pat([
        b("scout"),
        b("wild", "S", "SE"),
    ], [
        ["S"],
        ["S", "SE", "S"],
        ["S", "SE", "S", "SE"],
        ["S", "SE", "S", "SE", "S"],
        ["S", "SE", "S", "SE", "S", "SE"],
    ], [
        br("S", "SE"),
    ])),
    regular("pee-pee-pinata", "Pee-Pee Pinata", 3, 
    // Catalogue "15": placed resources now collect WITH the node's energy
    // zone one-time bonus, like the tile's own resource.
    "As soon as this fortress is implemented, distribute any combination of 5 one-time resources on unoccupied nodes in the highest two energy zones. If placed on a permanent resource, that node grants both the one-time and the permanent resources. If placed on a one-time resource, that node grants 2x the amount of resources. Placed resources are collected with the node's energy zone bonus.", 
    // Two stars three bent steps apart. Tightened on the 2026-08-22 pass:
    // the two far northern markers dropped (6 → 4 X), leaving the pair
    // beside the anchor star and the pair hugging the span to the far star.
    pat([
        b("wild"),
        b("wild", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NW"],
        ["S", "SW"],
        ["S", "SE"],
    ])),
    regular("rinsed-repeat-offender", "Rinsed Repeat Offender", 3, "Whenever you take two identical actions in a turn, add a token to this fortress (at most 1 token per turn). You may take additional actions in a turn by consuming tokens, 1 token each. Construct building actions are only identical if they are constructing the same type of building. Fortress effect actions are only identical if the same fortress effect is used twice. Barracks actions are only identical if you tap two barracks to attack, or tap two barracks to defend, but not one attacking and one defending action.", 
    // Original pattern (no PDF art yet), authored to the 2026-08-22 rules: the
    // SAME star twice, side by side — the card's "do it again" read — with a
    // mine two steps on down the chain. Two X markers flank the leading star,
    // one hugs the span and one trails past the mine. No bridges: the
    // catalogue "37" revision dropped the span between the two stars (and the
    // card went 2 → 3 obsidian). The footprint (buildings, wilds as jokers) is
    // distinct from every other card's up to translation, rotation and
    // mirroring.
    pat([
        b("wild"),
        b("wild", "S"),
        b("mine", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NW"],
        ["S", "SE", "NE"],
        ["S", "SE", "S", "SE"],
    ])),
];
// --- Card effect configuration (current sheet) ------------------------------
//
// What each card DOES, as serializable data attached to its definition below
// (`FortressCard.effects`) and stored with it in the versioned catalogue
// documents. Gameplay reads behavior from the card data — never from the card
// id — so a future catalogue version can retune thresholds/amounts (or swap an
// effect) as pure data. Win-predicate timing is the card's `winTiming` field.
const CARD_EFFECTS_CURRENT = {
    // --- Win (victory) deck ---------------------------------------------------
    // "Win when you have 5 tokens on this card."
    // "50": "a mine on a resource in the highest 2 energy zones" (`minLevel`);
    // "1"–"49" counted anything above forest.
    "enchanted-mines": {
        winAtTokens: 5,
        resetsEachRound: { tokens: true },
        hooks: [{ kind: "elevated_resource_mine_token", minLevel: ZONE_HIGHEST - 1 }],
    },
    // 2026-08-21 revision: only a defense held against the OWNER's attack
    // removes a token (was: any successful defense). Catalogue "33" cut the
    // win threshold from 6 tokens to 5.
    // "50": "Remove 1 token if anyone successfully defends a barracks attack" —
    // any held defence, not only against the owner's attack ("11"–"49").
    vacmakd: {
        winAtTokens: 5,
        hooks: [{ kind: "attack_destruction_token" }],
    },
    // "Place a token for every opponent's building that you destroy by any
    // means. Win at 6 tokens. Remove all tokens at the end of the round."
    // "50": win at 4 tokens (5 before).
    "giant-sphincter": {
        winAtTokens: 4,
        resetsEachRound: { tokens: true },
        hooks: [{ kind: "destroyer_token" }],
    },
    // Zapped Bapped Statue: "Whenever any player takes an energy-requiring
    // action, you may pay the exact same energy penalty cost and place tokens
    // on this card equal to the energy penalty paid." One mechanism for every
    // player, the owner included: a finalized energy action opens a pay window
    // the owner may answer until the next action finalizes — or, during
    // queued-move volleys, their auto-pay matches it. Win at 10 stored;
    // cleared each round.
    "bursted-flap": {
        winAtStored: 10,
        resetsEachRound: { stored: true },
        hooks: [{ kind: "opponent_penalty_charge_offer" }],
    },
    // "Place a token at the end of the round when you are the last player to
    // take an action. Once this reaches 2 tokens, win the game."
    "penguin-puncher": {
        winAtTokens: 2,
        hooks: [{ kind: "round_end_last_actor_token" }],
    },
    "obsidian-blimp-port": {
        winAtTokens: 2,
        destructible: { returnToPile: true },
        placement: { requireBuildingInZone: ZONE_HIGHEST },
        hooks: [{ kind: "round_end_token" }],
    },
    "traveling-booba": {
        grants: ["move_fortress"],
        // Catalogue "46": the once-per-round limit is a TOKEN, not a flip. The
        // move places one; the cap of 1 is what blocks a second move, and the
        // round reset below takes the token off again. ("18"–"45" exhausted the
        // card instead — `flipOnUse` + `resetsEachRound.disabled` — which is what
        // games pinned to those versions still read.)
        tokenCap: 1,
        // "built entirely in the lowest energy zone" = any zone whose construction
        // vigor cost is 0 (forest AND foothill), not merely altitude level 0.
        placement: { zone: { maxVigorPenalty: 0 } },
        // Catalogue "50": "Moving takes energy based on the highest energy zone
        // that you have moved into" — the climb charges again (the default), as
        // it did before "21" waived it (`moveFortress: { vigorPenalty: false }`,
        // "21"–"49"). The placement floor above is a build restriction, not a
        // penalty. The 1TR move token clears each round; the win is checked at
        // the start of the holder's turn ("50" timing badge).
        resetsEachRound: { tokens: true },
        destructible: {},
        win: { condition: { kind: "card_nodes_all_in_zone", level: ZONE_HIGHEST } },
    },
    "mirror-morse": {
        win: { condition: { kind: "mine_opposite_home" } },
    },
    "piercing-needle": {
        placement: { zone: { min: ZONE_HIGHEST } },
        win: { condition: { kind: "always" } },
    },
    "miner-bots": {
        gate: { kind: "mine_in_every_zone" },
        win: { condition: { kind: "always" } },
    },
    // "Can only be built if you have 8 or more fortresses under your control."
    // ("50"; 5 before.)
    "obsidian-pipeline-masterworks": {
        gate: { kind: "fortress_count_at_least", count: 8 },
        win: { condition: { kind: "always" } },
    },
    "exodial-the-forsaken-one": {
        placement: { useOpponentBuildings: true },
        win: { condition: { kind: "always" } },
    },
    // Catalogue "12"–"17" printed "within your defence range" (`useDefendRange`);
    // "18" puts the trace back on the Barracks ATTACK stat, so the flag is gone
    // — pinned games keep whichever stat their snapshot carries. The same
    // revision raises `minOpponent` 1 -> 2 ("at least 2 of the fortresses must
    // be opponent fortresses"), costs 13 obsidian (was 12), and reads "a
    // building" as a NAKED one: `freeSourcesOnly` bars a source welded into any
    // fortress, so the card can no longer trace from its own pieces. The
    // fortresses COUNTED are unchanged — the owner's own still count toward the
    // 3, so the cheapest shape is a loose building reaching one of theirs plus
    // two opponents'.
    // "50": "a building within your defend range to 3 fortresses" — DEFEND
    // range again (as "12"–"17"), traced from any building, welded or not.
    "parasitic-breakfast": {
        win: {
            condition: {
                kind: "fortresses_in_attack_range",
                count: 3,
                minOpponent: 2,
                useDefendRange: true,
            },
        },
    },
    // 2026-08-21 revision: the zone's outward paths — the ones crossing into
    // different energy zones — must be intact (bridged) too.
    // "50": "the highest energy zone has all their nodes connected. Nodes must
    // be connected between the highest and second highest energy zone as well"
    // — only the summit counts (`level`); its crossings into the zone below are
    // the `acrossZones` edges.
    "cg-builder-of-bridges": {
        win: {
            condition: { kind: "any_zone_fully_connected", acrossZones: true, level: ZONE_HIGHEST },
        },
    },
    // "Occupy 72/X tiles, where X equals the number of players, rounded up."
    // "50": the home-base clause left the text — any tile with a building counts.
    "big-sprawler": {
        win: { condition: { kind: "occupied_tiles_per_player", tiles: 72, anywhere: true } },
    },
    "underhand-skyscraper": {
        win: { condition: { kind: "sole_occupier_of_zone", level: ZONE_HIGHEST } },
    },
    // "10 mineral, 10 food and 10 energy in your bank."
    // "50": 15 of each (the "49" text printed 12 over a 10/10/10 config).
    bankman: {
        win: { condition: { kind: "bank_at_least", amounts: [15, 15, 15, 0] } },
    },
    // "10 obsidian production and 10 obsidian banked."
    "plink-harvester": {
        win: {
            condition: {
                kind: "production_and_bank_at_least",
                resource: 3,
                production: 10,
                bank: 15, // "50" (the "49" text printed 12/12 over a 10/10 config)
            },
        },
    },
    "booba-farmer": {
        win: { condition: { kind: "bank_exceeds_all_others" } },
    },
    // Catalogue "15" rework: the flat "5 Obsidian less" became a growing
    // discount — win-deck cards cost the holder [fortresses implemented this
    // game, by anyone, cumulatively] Obsidian less, floored at 1
    // (`GameState.fortressesImplemented` feeds `fortressCostFor`). Games
    // pinned to "14" and earlier keep the flat discount from their
    // snapshotted effects.
    "inverse-molehill": {
        fortressDiscount: {
            deck: "win",
            obsidian: 0,
            minObsidian: 1,
            perImplementedFortress: true,
        },
    },
    // 2026-08-21 revision: "4 UNIQUE artifacts" — duplicate copies of one
    // artifact (BAM Exploder can build endless Artifacts of Courage) count
    // once; the win needs 4 distinct artifact cards.
    "artifact-of-speed": {
        tags: ["artifact"],
        win: {
            condition: { kind: "tagged_count", tag: "artifact", count: 4, unique: true },
        },
        hooks: [{ kind: "implement_grant", amount: [1, 0, 0, 0] }],
    },
    "artifact-of-courage": {
        tags: ["artifact"],
        win: {
            condition: { kind: "tagged_count", tag: "artifact", count: 4, unique: true },
        },
        hooks: [{ kind: "implement_grant", amount: [0, 1, 0, 0] }],
    },
    "artifact-of-knowledge": {
        tags: ["artifact"],
        win: {
            condition: { kind: "tagged_count", tag: "artifact", count: 4, unique: true },
        },
        hooks: [{ kind: "implement_grant", amount: [0, 0, 1, 0] }],
    },
    // The sheet revision swapped the gains: Plebians now grants the "1 of any
    // resource" pick; Atrocity grants 1 obsidian.
    "artifact-of-plebians": {
        tags: ["artifact"],
        win: {
            condition: { kind: "tagged_count", tag: "artifact", count: 4, unique: true },
        },
        implementChoice: "resource",
        hooks: [{ kind: "implement_grant_choice" }],
    },
    "artifact-of-atrocity": {
        tags: ["artifact"],
        win: {
            condition: { kind: "tagged_count", tag: "artifact", count: 4, unique: true },
        },
        hooks: [{ kind: "implement_grant", amount: [0, 0, 0, 1] }],
    },
    // BAM Exploder (new on the 2026-08-21 sheet): the holder may implement
    // Artifact of Courage any number of times from outside the market (each
    // build pays its printed cost and fires its implement hooks — the "full
    // effect"); and at round end every opponent fortress within the owner's
    // attack range of any of their Artifacts of Courage is destroyed, +1
    // token each — governed by the owner's auto-destroy toggle
    // (`Player.artifactAutoDestroy`, default on). Win at 3 tokens. (The
    // card's earlier draft also granted the `attack_fortress_nodes` passive —
    // "You may destroy opponent fortresses" — dropped from the printed text;
    // the passive stays implemented as unused vocabulary.)
    // 2026-08-21 revision (second pass): the blast now catches ALL fortresses
    // in range — the owner's own included, each Artifact of Courage itself
    // among them ("destroy all fortresses (including itself)").
    "bam-exploder": {
        winAtTokens: 3,
        alwaysBuildable: ["artifact-of-courage"],
        hooks: [
            {
                kind: "round_end_ranged_fortress_destroy",
                fromCardId: "artifact-of-courage",
                includeOwn: true,
            },
        ],
    },
    // Corrupt Hermit (new on the 2026-08-21 sheet): holding it lets EVERY
    // fortress the owner implements mix their own and opponents' free
    // buildings (Exodial's relaxation, holder-scoped). Catalogue "45" settled
    // what that does to the piece: NOTHING changes hands. A conscripted
    // building stays its owner's for every rule — their production, their build
    // range, their defending — and only the FORTRESS is the Hermit holder's
    // (`fortressOwnerId`). What the owner loses is the right to take it away:
    // a building standing in someone else's fortress can't be sold, moved or
    // attacked, like any fortress piece. ("1"-"44" CAPTURED instead, moving
    // `ownerId` to the holder; the change is in command code, not this config,
    // so it reaches pinned games too.) The relaxation is a COMBINATION, so at
    // least one realised cell must be the builder's own. +1 token whenever a
    // fortress the owner builds used at least one opponent building — bridge
    // cells never count, bridges have no owner. Win at 3 tokens.
    // "50": win at 2 tokens (3 before).
    "corrupt-hermit": {
        winAtTokens: 2,
        implementUseOpponentBuildings: true,
        hooks: [{ kind: "opponent_piece_implement_token" }],
    },
    // Singularity Ball (catalogue "15" rework — was "35 banked resources of
    // one type"): every Food the OWNER's payments actually deduct
    // (substitutions counted as what really left the bank; other players'
    // spending never counts) lands on the Ball's stored pile; win at 30
    // stored (catalogue "41"; "18"-"40" won at 25, before that 40). Checked at
    // every immediate seam like all stored thresholds.
    "singularity-ball": {
        winAtStored: 30,
        hooks: [{ kind: "food_spent_stored" }],
    },
    // CurdleCat (new on the 2026-08-21 sheet, second pass): the copy action
    // AWARDS the player a phantom card instance carrying the copied card's id
    // (`FortressCardState.copied`) — a full card for every existing rule with
    // its own fresh state, holding no board nodes. Effect-only by
    // construction: nothing of the target's state travels, and the phantom's
    // state never mingles with CurdleCat's own tokens (the copy-use counter,
    // capped at 2 by this entry). Switching destroys the previous phantom;
    // the phantom dies with its CurdleCat (linked via `copiedCardId`).
    // Printed tie-break (2026-08-22 revision): "if a win condition is met for
    // both the original and the copied fortress at the exact same time, the
    // original fortress takes priority" — the win scan (`findWinner`) defers a
    // firing phantom to a simultaneously-firing original of the same card.
    curdlecat: {
        grants: ["copy_fortress_effect"],
        tokenCap: 2,
    },
    // Dog Den (new on the 2026-08-21 sheet, second pass; third pass reworked
    // the reshuffle and re-costed 5 → 8 obsidian; fourth pass made the
    // reshuffle per-card): "Gain an action" — the owner picks WHEN to mark.
    // The action names one face-up card per row (appended to the instance's
    // `markedCardIds`, shown to everyone).
    //
    // Catalogue "28"+ (`markShufflePerCard`) settles each MARKED card on its
    // own — "you may shuffle 0, 1 or both" — so the marker can bury the one
    // their opponents could reach and leave the other showing for themselves.
    // A shuffled card leaves its row (its slot refilling from its deck) and
    // joins that deck; a kept card doesn't move, staying face up and buildable
    // where it sits, and the rest of the market (rows, discards, locks) never
    // moves either way. Catalogue "24"–"27" (`markShuffleOptional`) asked the
    // same question once for both marks; pre-"24" games always shuffle.
    //
    // +1 token per use, capped at 1 — but a Tokenizer 9000 removing the token
    // re-arms the action, so marks ACCUMULATE across uses. The win needs only
    // ONE marked win-deck card and ONE marked regular-deck card implemented
    // (by anyone): each implement of a marked card latches that deck's flag on
    // the instance, and the win fires once both flags are set.
    // "50": the mark is 1TR — the use token clears each round (once per game
    // before).
    "dog-den": {
        grants: ["mark_fortresses"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        markShuffleOptional: true,
        markShufflePerCard: true,
        win: { condition: { kind: "marked_both_decks_implemented" } },
        hooks: [{ kind: "marked_fortress_implemented" }],
    },
    // Stab Pole Hole (new on catalogue "15"): "Gain an action: designate one of
    // your buildings..." — the designate_attack_target action force-taps every
    // opponent Barracks with the target in its attack range (re-aiming already
    // committed ones), then opens a free mid-turn defense window for the
    // designator. A defense held on the designated node (once per node per
    // round, like every held defense) is +1 token via the hook; win at 5.
    // No per-round cap — each action-costed use just re-points the forced
    // aims. Availability (unprinted): only while an opponent Barracks can
    // reach one of the owner's buildings.
    "stab-pole-hole": {
        grants: ["designate_attack_target"],
        winAtTokens: 5,
        hooks: [{ kind: "designated_defense_token" }],
    },
    // Space Cheese Assassin (new on catalogue "34"): implementing it shuffles a
    // DECOY copy of the same card into the win deck (`implement_decoy_copy`).
    // The decoy is a second occurrence of the id in the pile — the real card
    // left the market for good when it was implemented, so anything answering
    // to this id in a deck is the decoy. It is intercepted the moment a draw
    // would reveal it: it never reaches a market row or a hand (hence "can
    // never be implemented", which `implementFortress` also refuses outright)
    // and goes straight to the discard pile, from where the next reshuffle
    // sends it round again. A reveal caused by anyone but this card's owner
    // pays out: +1 token here, and 3 resources move from that player to the
    // owner — the VICTIM picks which, unlike a combat plunder. Win at 3 ("15"–
    // "49"); catalogue "50" wins at 2 and steals 2.
    "space-cheese-assassin": {
        winAtTokens: 2,
        hooks: [{ kind: "implement_decoy_copy", steal: 2, decoyCardId: "space-cheese-spy" }],
    },
    // The Spy (catalogue "50"): the Assassin's decoy as a card of its own —
    // never dealt, never implementable; drawn from a pile it burns to the win
    // discard and pays the Assassin's owner (see `drawSkippingDecoys`).
    "space-cheese-spy": {
        decoyFor: "space-cheese-assassin",
    },
    // Space Cheese Curdler (new on catalogue "15"): pure interception — no
    // win condition of its own. When another player meets one, the holder's
    // intact (un-flipped) Curdler postpones it and the holder plays a whole
    // round alone: the collection step, their round-scoped resets, the cycle
    // step, round-start effects, 3 turns, then round-end effects and the
    // end-of-round win check — only then does the postponed win execute (the
    // room's last-stand sequence; the instance flips as its one use).
    //
    // Catalogue "15"–"20" printed `{ extraActions: 3 }` instead: ONE turn with
    // 3 bonus action points and no round seams at all, so a start-of-turn or
    // end-of-round win condition had nothing to fire on. Pinned games keep it.
    // Catalogue "50": the solo round is 2 turns ("21"–"49" gave 3).
    "space-cheese-curdler": {
        lastStand: { turns: 2 },
        // "One use." — `interceptWinForLastStand` exhausts the instance itself;
        // the flag documents that (nothing reads it for this card) so the card
        // face can badge the flip.
        flipOnUse: true,
    },
    // --- The two cards new on the 2026-10-17 sheet (catalogue "50") ------------
    // "Win if you have buildings/fortresses within your scout building range to
    // all opponent home bases" — immediate. The printed 50 obsidian is "50/X":
    // split across the seated players, rounded up.
    "horny-neighbour": {
        costSplitByPlayers: true,
        win: { condition: { kind: "scout_range_to_all_opponent_homes" } },
    },
    // "Win if you have a fortress fully contained in every energy zone" — end
    // of round.
    "chicken-dinner": {
        win: { condition: { kind: "fortress_fully_in_every_zone" } },
    },
    // --- Regular (effect) deck ----------------------------------------------
    // "Put 5 tokens on this card" = 5 total uses (tokens count up to the cap).
    // "One of your buildings" (2026-08-21 revision — was Scouts only): the
    // protect may land on any building you own, the pieces of a destructible
    // fortress (Lone Traveler, Obsidian Blimp-port) included — those can lose a
    // building and unravel. "Or X markers" (2026-09-10, catalogue "39"+) widens
    // it once more to the fortress-stamped empty nodes — worth covering on a
    // DESTRUCTIBLE fortress; a hardened one's own X's are already beyond reach.
    // It is a plain on-turn action costing
    // one action point ("as an action"), never a defense-window reaction, so the target
    // need not be under attack. Upkeep is untouched by protection: an
    // indestructible attacking Barracks still owes its Food upkeep and starves
    // (self-destructs) unpaid, exactly as the card now prints.
    // --- Regular deck — catalogue "49" (2026-10-16 overhaul) -------------------
    //
    // Every regular card now prints an ACTIVE effect (a granted action, with a
    // use budget printed as "NT" = N uses for the whole game / "NTR" = N uses
    // per round) and a PASSIVE effect. The use budget is the card's `tokenCap`;
    // "TR" adds `resetsEachRound.tokens`. The old flip-on-use once-per-round
    // bookkeeping (`flipOnUse` + `resetsEachRound.disabled`) survives only on
    // Swole Turtle, whose tokens ARE its energy store, and on the snapshots of
    // earlier catalogue versions, which the handlers still honour
    // (`consumeUse`). Every regular card's Active is modelled: the last two
    // holdouts — Bridgette's ranged free bridge ("52") and Dungeon Dice
    // Martyr's implement-from-discard ("53") — have their grants now.
    //
    // The "Gain +N banked X. Or gain +1 X production" passives are an
    // implement-time pick (`implementChoice` of kind "bank_or_production") paid
    // out by the matching `implement_bank_or_production` hook.
    // Active: "Make one of your destructible buildings indestructible. 2T" —
    // 2 protections for the whole game (was 4; the "39"-"48" X-marker reach is
    // gone with the new text's "buildings"). Protection stays permanent.
    // Passive: "+2 banked minerals, food or energy. Or +1 mineral, food or
    // energy production" — 2 of a chosen kind or +1 production of one; never
    // obsidian ("56"; "49"-"55": 2 any-resource picks, which could be obsidian,
    // cashed through `pick_bonus_resources`).
    "godspeed-machine": {
        grants: ["make_scout_indestructible"],
        tokenCap: 2,
        protectAnyBuilding: true,
        implementChoice: { kind: "bank_or_production", bank: 2, resources: [0, 1, 2] },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resources: [0, 1, 2] }],
    },
    // Passive: "Build bridges using any resource. Building/attacking/defending
    // over bridges does not take any range" — the "33"+ pair of passives.
    // Active: "Build a bridge with +4 building range and no action cost. 1TR"
    // — a `free_bridge` at the Bridge's build range +4 that pays the Bridge's
    // (wildcard) resource cost but no action point, once a round. Modelled in
    // catalogue "52"; "33"-"51" printed the line but carried no grant.
    "bridgette-the-beauty": {
        passives: [{ kind: "bridge_any_resource" }, { kind: "free_range_over_bridges" }],
        grants: ["free_bridge"],
        freeBridge: { rangeBonus: 4 },
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Build a building that collects twice as many one-time resources.
    // 4T" — the build-time rider, 4 uses for the game. Passive: "+2 scout range".
    "energized-bun-bun": {
        rider: "double_one_time",
        tokenCap: 4,
        passives: [{ kind: "scout_range", amount: 2 }],
    },
    // Hurtful Shrine. Active: "Build a barracks with +2 range and no energy
    // penalties. 1TR" — a `free_build` that is Barracks-only, pays the
    // Barracks' resource cost, skips the Vigor penalty, reaches 2 further than
    // the Barracks' own build range ("49"-"53": 4), and spends an action point.
    // Passive: the "35"+ defend-range claim, cashed via `claim_resources`.
    icarlie: {
        grants: ["claim_resources", "free_build"],
        freeBuild: { building: "barracks", rangeBonus: 2, payCost: true },
        grantCostsAction: true,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        hooks: [{ kind: "build_in_defend_range_claim" }],
    },
    // Active: "Gain 2x the amount of energy stored on this card. You may not
    // deposit energy this round. 1TR." Passive: "Gain +1 building range. You
    // may deposit any amount of banked energy onto this card at the end of the
    // round." The deposit
    // resolves from the owner's standing energy reserve
    // (`Player.vigorDepositFloor`) onto the card's STORED pile (`toStored`);
    // the collect is a 1TR use — its token is the "collected this round"
    // marker the deposit hook checks, cleared at round start. ("7"-"48" kept
    // the energy as the token count and flipped the card on collect.)
    "swole-turtle": {
        passives: [{ kind: "building_range", amount: 1 }],
        grants: ["collect_card_energy"],
        hooks: [{ kind: "round_end_vigor_deposit", toStored: true }],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Place a copy of this fortress anywhere in the same energy level
    // as where it was initially implemented. You cannot destroy buildings with
    // this action. 1TR." — once per round now (was once per game), still an
    // action point; the replica never demolishes (its X needs an empty node).
    // Passive: "+3 minerals. Or +1 mineral production" — picked on implement.
    "hole-worm": {
        grants: ["replicate_fortress"],
        placement: { sameZone: true },
        grantCostsAction: true,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        implementChoice: { kind: "bank_or_production", bank: 3, resource: 0 },
        hooks: [{ kind: "implement_bank_or_production", bank: 3, resource: 0 }],
    },
    // Active: "Double all banked resources. 1T." — once per game, token-counted.
    // Passive: "+2 banked resources" — 2 free any-resource picks on implement.
    "skippity-star": {
        grants: ["double_banked_resources", "pick_bonus_resources"],
        tokenCap: 1,
        hooks: [{ kind: "implement_grant_picks", picks: 2 }],
    },
    // Active: "Build a mine without action or building costs. Energy penalties
    // still apply. 1TR" ("54" wording; same rule) — a Mine-only `free_build` at
    // the Mine's own range: no resources, no action point, the zone's Vigor
    // penalty paid as usual. Passive: "+2 banked energy. Or +1 energy
    // production" — picked on implement. The "20"-"48" mine discount and the
    // lowest-minerals income are gone with the new text.
    "resource-sequencer": {
        grants: ["free_build"],
        freeBuild: { building: "mine", payVigorPenalty: true },
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        implementChoice: { kind: "bank_or_production", bank: 2, resource: 2 },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resource: 2 }],
    },
    // Active: "Lock/unlock a fortress card. This does not consume an action.
    // 1TR". Passive: obsidian as any resource.
    "conversion-treadmill": {
        passives: [{ kind: "obsidian_wildcard" }],
        grants: ["set_fortress_lock"],
        grantCostsAction: false,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Gain +1 energy. 1TR" — the `bank_two_resources` grant with a
    // fixed 1-vigor payout (no pick). Passive: penalties 1/3/5 -> 1/2/3.
    "plasmamagnetic-waves": {
        passives: [{ kind: "penalty_reduced" }],
        grants: ["bank_two_resources"],
        bankGrant: { amount: 1, resource: 2 },
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Build a building without any energy penalties. 4T" — the token
    // stands in for the penalty, 4 uses for the game (was 5). Passive: "+2
    // banked energy. Or +1 energy production" — picked on implement.
    "kinetic-absorption-facility": {
        grants: ["pay_penalty_with_token"],
        tokenCap: 4,
        implementChoice: { kind: "bank_or_production", bank: 2, resource: 2 },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resource: 2 }],
    },
    // Active: "Untap 1 of your barracks. Or perform an action without consuming
    // an action. 1TR" ("50" wording; "49" printed the options the other way
    // round) — two grants sharing one token (either use takes it).
    // Passive: food <-> energy interchangeable. "54" drops the "cannot be built
    // in the lowest energy level" placement gate ("49"-"53" kept it from the
    // older printing although the text no longer said so).
    "transmutation-bench": {
        passives: [{ kind: "food_energy_interchange" }],
        grants: ["gain_free_action", "untap_barracks"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: free action OR untap 1 barracks, 1TR. Passive: "+2 attack & defend
    // range. After successfully destroying an opponent building, you may give
    // them 2 of any resource to take over their building" — the capture
    // setting (`Player.captureAuto` + `captureGiveResource`) paying 2 ("54";
    // "14"-"53" paid 1); the through/across relaxations of "16"-"48" are gone
    // with the new text.
    "telescopic-arm": {
        passives: [
            { kind: "attack_range", amount: 2 },
            { kind: "defend_range", amount: 2 },
            { kind: "capture_on_destroy", amount: 2 },
        ],
        grants: ["gain_free_action", "untap_barracks"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: free action OR untap 1 barracks, 1TR ("54"; "49"-"53" printed
    // the free action only). Passive: "+2 attack & defend range. You may
    // attack & defend through buildings, fortresses and bridge gaps."
    "porkypine-neck": {
        passives: [
            { kind: "attack_range", amount: 2 },
            { kind: "defend_range", amount: 2 },
            { kind: "attack_through_obstructions" },
            { kind: "attack_across_gaps" },
            { kind: "defend_through_obstructions" },
            { kind: "defend_across_gaps" },
        ],
        grants: ["gain_free_action", "untap_barracks"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Lock/unlock a fortress card. This does not consume an action.
    // 2TR" — two uses a ROUND now (was two a turn). Passive: "Whenever any
    // player implements a locked fortress, gain 2 of any resource and add/remove
    // a token from a fortress you own" — the 2 resources are banked as picks,
    // and ("54"+) the token change is banked on the card's `stored` pile, cashed
    // free through its `adjust_fortress_token` grant on the holder's turn —
    // REGULAR-deck fortresses only, like every token add/remove (Wei,
    // 2026-10-20). "49"-"53" printed the rider but carried none.
    "supreme-bondage": {
        grants: ["set_fortress_lock", "pick_bonus_resources", "adjust_fortress_token"],
        uncappedGrants: ["adjust_fortress_token"],
        tokenAdjustFromStored: true,
        adjustRegularOnly: true,
        grantCostsAction: false,
        tokenCap: 2,
        resetsEachRound: { tokens: true },
        hooks: [{ kind: "locked_implement_picks", picks: 2, tokenAdjusts: 1 }],
    },
    // Active: "Cycle as many cards as you want from one row without any cost.
    // 3T." — the whole-row free cycle, 3 uses for the GAME (was once a round);
    // "54" prints the one-row limit the action always had. Passive: "+1
    // action per turn".
    cyclopz: {
        grants: ["free_cycle_market"],
        tokenCap: 3,
        passives: [{ kind: "extra_actions", amount: 1 }],
    },
    // Curious Climb. Active: the top-5 peek — "re-arrange them or move any of
    // them to the bottom of the pile", no discard routing since "54"
    // (`reorderBottomOnly`; "14"-"53" could also discard) — then "cycle up to 2
    // fortresses from the current deck for free" (was 1), once a round.
    // Passive: "+1 building range".
    "curious-clit": {
        grants: ["reorder_fortress_deck"],
        tokenCap: 1,
        reorderGlanceCount: 5,
        reorderPeek: true,
        reorderBottomOnly: true,
        reorderCycleCount: 2,
        resetsEachRound: { tokens: true },
        passives: [{ kind: "building_range", amount: 1 }],
    },
    // Active: free action OR untap 1 barracks, 1TR. Passive: "Gain 1 extra
    // action per turn" — the holder only; the "everyone gets 3" allowance and
    // the paid extra actions of "1"-"48" are gone with the new text.
    "action-blaster": {
        grants: ["gain_free_action", "untap_barracks"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        passives: [{ kind: "extra_actions", amount: 1 }],
    },
    // Active: "End the current round and take the first turn marker. 1T".
    // Passive: the holder's end-of-round wins take priority over everyone
    // else's, always (`end_of_round_win_priority`, "54"+). "49"-"53" carried no
    // passive: priority came only from the first-player marker, i.e. only
    // after the forced end handed it to them.
    "times-up": {
        grants: ["force_end_round"],
        tokenCap: 1,
        passives: [{ kind: "end_of_round_win_priority" }],
    },
    // Active: "Add/remove a token from a regular fortress you own. 1TR" — own
    // REGULAR-deck fortresses only. Passive: "Actions that add/remove tokens
    // from fortresses no longer cost actions for you".
    "to-the-moon-tokenizer-9000": {
        grants: ["adjust_fortress_token"],
        adjustRegularOnly: true,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        passives: [{ kind: "token_actions_free" }],
    },
    // Active: free action OR untap 1 barracks, 1TR. Passive: "+3 building range
    // and +1 attack & defend range" (was +2 / +2 / +2).
    "trifecta-sphinx": {
        passives: [
            { kind: "building_range", amount: 3 },
            { kind: "attack_range", amount: 1 },
            { kind: "defend_range", amount: 1 },
        ],
        grants: ["gain_free_action", "untap_barracks"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Build any building with +4 building range. 1TR" — a `free_build`
    // of any Scout / Barracks / Mine that pays its full cost and Vigor penalty
    // and spends an action point; only the reach is boosted. Passive: "+4
    // building range for your scouts and bridges" ("54"; "49"-"53": +5) — the
    // through-obstruction line of "1"-"48" is gone with the new text.
    "rabid-shoes": {
        passives: [{ kind: "scout_bridge_range", amount: 4 }],
        grants: ["free_build"],
        freeBuild: { rangeBonus: 4, payCost: true, payVigorPenalty: true },
        grantCostsAction: true,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Active: "Add/remove a token from a regular fortress you own. 2T" — two
    // uses per GAME ("54"; "51"-"53" printed 1T, "49"-"50" 1TR, clearing each
    // round). Passive: "+1 obsidian per implemented fortress at the start of
    // the round".
    "clobbering-bat": {
        hooks: [{ kind: "round_start_obsidian_per_fortress" }],
        grants: ["adjust_fortress_token"],
        adjustRegularOnly: true,
        tokenCap: 2,
    },
    // Active: "Gain resources equal to your production. 1T" — the production
    // payout at 1x with the track left standing (the "21"-"48" 2x-and-reset
    // trade keeps `reset` absent on those snapshots). Passive: "+1 of any
    // production" — the implement action's resource pick.
    "sudoku-bear": {
        grants: ["bank_production_reset"],
        bankProductionReset: { multiplier: 1, reset: false },
        tokenCap: 1,
        implementChoice: "resource",
        hooks: [{ kind: "implement_grant_production_choice" }],
    },
    // Active ("54"): "Shuffle any fortress from the discard pile back into its
    // corresponding deck. Then, cycle up to 2 cards for free. 1TR" — the
    // `shuffle_discard_into_deck` grant: a card from either row's discard pile
    // is shuffled into that row's deck, then up to `shuffleDiscardCycleCount`
    // face-up cards OF THAT SAME ROW cycle out free of their cycle cost (Wei,
    // 2026-10-20). One use a round; the action itself costs an action point.
    // Passive: "+2 banked
    // obsidian or +1 obsidian production", picked on implement.
    // "53" printed "Implement a fortress in the discard pile. 2T" (the
    // `implement_from_discard` grant, which stays live for games pinned to it)
    // with a flat +1 obsidian production; the face-down hand and the martyr
    // discard of "1"-"48" are older still.
    "dungeon-dice-martyr": {
        grants: ["shuffle_discard_into_deck"],
        shuffleDiscardCycleCount: 2,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        implementChoice: { kind: "bank_or_production", bank: 2, resource: 3 },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resource: 3 }],
    },
    // Catalogue "19": "select either scouts no longer cost actions or bridges
    // no longer cost actions for the rest of the game. Place a scout or bridge
    // on this card to denote which one was chosen." One pick on implement
    // (`implementChoice` of kind "piece"); the matching free-action passive is
    // the only one live (`ifChoice`). ("14"–"18" freed BOTH; "1"–"13" bridges
    // only.)
    // ARCHIVED in catalogue "49" (see `archivedRegularCards`) — kept because
    // matches pinned to "48" and earlier still play the card.
    "shifty-engineer": {
        implementChoice: { kind: "piece", options: ["scout", "bridge"] },
        passives: [
            { kind: "scout_free_action", ifChoice: "scout" },
            { kind: "bridge_free_action", ifChoice: "bridge" },
        ],
    },
    // "You may build fortresses with one less building/bridge piece. You decide
    // which piece is removed from the configuration."
    // ARCHIVED in catalogue "43" (see `archivedRegularCards`) — kept because
    // matches pinned to "42" and earlier still play the card, so the implement
    // action's `omit` field and the placement bar's piece-skipping stay live.
    "obsidian-ovary-overload": {
        implementOmitPiece: true,
    },
    // "Place any building for free, without requiring resources, vigor
    // penalties, or actions. Use your barracks defend range as the building
    // range. Place a token on this card when you do so. You cannot exceed 2
    // tokens" ("18"-"48"; the cap was 3 through "20").
    // ARCHIVED in catalogue "49" (see `archivedRegularCards`) — kept because
    // matches pinned to "48" and earlier still play the card; the
    // `barracks_defend` free-build geometry stays live for them.
    "block-us-baby": {
        grants: ["free_build"],
        freeBuild: { range: "barracks_defend" },
        tokenCap: 2,
    },
    // Active: "Sell one of your buildings for 100% of its build cost. Energy is
    // not refunded, and one-time resources cannot be re-collected. 1TR" — one
    // sale a round (was four). Passive: "+1 additional action per turn".
    recyclomaniac: {
        grants: ["sell_building"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        passives: [{ kind: "extra_actions", amount: 1 }],
    },
    // Active: the deck search, 1 use for the game (was 3) — of the REGULAR
    // deck only since "54" ("the regular fortress decks"; either deck
    // before). Passive: "+1 banked obsidian" on implement.
    "poopy-investigator": {
        grants: ["tutor_market_card"],
        tutorMarkets: ["REGULAR"],
        tokenCap: 1,
        hooks: [{ kind: "implement_grant", amount: [0, 0, 0, 1] }],
    },
    // Active: the once-a-round building move, token-counted now. Passive: "+1
    // mine building range and +1 attack & defend range" ("50"; "49" gave the
    // mine range only).
    "nomadic-pufferfish": {
        grants: ["move_building"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        passives: [
            { kind: "mine_range", amount: 1 },
            { kind: "attack_range", amount: 1 },
            { kind: "defend_range", amount: 1 },
        ],
    },
    // Active: "Lock/unlock a fortress card. This does not consume an action.
    // 1TR". Passive: "Food-consuming actions cost 1 less (minimum 1)" — the
    // extra plunder of "14"-"48" is gone with the new text.
    "hippoman": {
        passives: [{ kind: "food_costs_reduced", amount: 1 }],
        grants: ["set_fortress_lock"],
        grantCostsAction: false,
        tokenCap: 1,
        resetsEachRound: { tokens: true },
    },
    // Catalogue "17": "+1 resource of any kind for each opponent building
    // WITHIN YOUR MINE RANGE to one of your buildings at the start of the
    // round. Max 1 per opponent building." — the "14"-"16" printing paid on
    // bare adjacency and skipped the owner's fortress pieces; `range: "mine"`
    // widens it to the owner's real Mine build geometry, fortresses (and their
    // X markers) counting as sources like any other build point. Banked as
    // single-resource picks, cashed via the free pick_bonus_resources action.
    // ARCHIVED in catalogue "31" (see `archivedRegularCards`) — kept because
    // matches pinned to "30" and earlier still play the card.
    "frienemy": {
        grants: ["pick_bonus_resources"],
        hooks: [{ kind: "round_start_picks_per_adjacent_opponent", range: "mine" }],
    },
    // Active: "+2 of any single resource. 1TR" — token-counted now. Passive:
    // "+1 building range".
    "morphing-midget": {
        grants: ["bank_two_resources"],
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        passives: [{ kind: "building_range", amount: 1 }],
    },
    // "Whenever you take two identical actions in a turn, add a token to this
    // fortress... You may take additional actions in a turn by consuming
    // tokens." The repeat seam fires once a turn (the room's per-turn tally), so
    // the hook needs no cap of its own; tokens are a spendable pool, never a use
    // counter, so the card prints NO `tokenCap` and never resets.
    // ARCHIVED in catalogue "49" (see `archivedRegularCards`) — kept because
    // matches pinned to "48" and earlier still play the card.
    "rinsed-repeat-offender": {
        grants: ["spend_token_for_action"],
        hooks: [{ kind: "repeat_action_token" }],
    },
    // Gut Re-arranger (Demented Rapist before "49"). Active: "Redistribute your
    // resources however you want to. 1T" — the token IS the use counter.
    // Passive: "Gain 1 mineral every time you build on an empty node" — a node
    // with no tile resource under it.
    "demented-rapist": {
        grants: ["redistribute_resources"],
        tokenCap: 1,
        hooks: [{ kind: "build_on_empty_node_mineral" }],
    },
    // "As soon as this fortress is implemented, distribute any combination of
    // 5 one-time resources on unoccupied nodes in the highest two energy
    // zones..." — banked on implement, placed via the free
    // drop_one_time_resources action (zone floor is hook data).
    // ARCHIVED in catalogue "49" (see `archivedRegularCards`) — kept because
    // matches pinned to "48" and earlier still play the card.
    "pee-pee-pinata": {
        grants: ["drop_one_time_resources"],
        hooks: [
            { kind: "implement_grant_resource_drops", amount: 5, minZoneLevel: 3 },
        ],
    },
    // --- The four cards new on the 2026-10-16 sheet (catalogue "49") -----------
    // Active: "Build a scout with +3 range. Do not pay building or energy
    // costs. 2T" — a Scout-only `free_build` at the Scout's range +3, free of
    // resources and Vigor, still an action point; 2 uses for the game.
    // Passive: "+2 banked minerals, food or energy. Or +1 mineral, food or
    // energy production" — on implement, 2 of a chosen kind or +1 production of
    // one; never obsidian ("55"; "49"-"54": 2 any-resource picks, which could
    // be obsidian, cashed through `pick_bonus_resources`).
    "apex-butt-blast": {
        grants: ["free_build"],
        freeBuild: { building: "scout", rangeBonus: 3 },
        grantCostsAction: true,
        tokenCap: 2,
        implementChoice: { kind: "bank_or_production", bank: 2, resources: [0, 1, 2] },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resources: [0, 1, 2] }],
    },
    // Active: "All other players get +1 mineral. You get +3 minerals. 1TR".
    // Passive: "Every time you build on a one-time resource, you may
    // cycle/lock/unlock a card" — each such build banks one reaction
    // (`Player.pendingMarketActions`), cashed free through `market_reaction`
    // (a cycle of one card, or a lock/unlock), which no token gates.
    "miss-mineralverse-maggot": {
        grants: ["mineral_windfall", "market_reaction"],
        uncappedGrants: ["market_reaction"],
        mineralWindfall: { self: 3, others: 1 },
        tokenCap: 1,
        resetsEachRound: { tokens: true },
        hooks: [{ kind: "one_time_build_market_action" }],
    },
    // Active: "Add or remove up to 3 tokens from your regular fortresses. 1T"
    // — one `adjust_fortress_tokens` use carrying up to `tokenAdjustments`
    // unit changes across the holder's OTHER regular fortresses, the use
    // counted by the card's own single token ("53"+; "49"-"52" re-distributed
    // a token reservoir the card held and flipped on use). Passive: "Double or
    // halve the token capacity of another regular fortress you own" — an
    // implement-time pick recorded on the chosen fortress (`capScale`) for the
    // rest of the game; no other regular fortress held, nothing to rescale.
    // Both halves reach REGULAR-deck fortresses only (`adjustRegularOnly`).
    "big-boom": {
        grants: ["adjust_fortress_tokens"],
        tokenAdjustments: 3,
        adjustRegularOnly: true,
        tokenCap: 1,
        implementChoice: { kind: "scale_token_cap" },
    },
    // Active: "Re-enter the round if you already passed. You get +3 actions on
    // the turn that you re-enter. 2T" — the rejoin takes a token (2 for the
    // game as in "50"-"53"; "49" and "54"-"55" printed 3T) and banks the +3 for
    // the re-entry turn. Passive: "+2 banked obsidian. Or +1 obsidian
    // production", picked on implement.
    "grand-entrance": {
        grants: ["rejoin_round"],
        rejoinBonusActions: 3,
        tokenCap: 2,
        implementChoice: { kind: "bank_or_production", bank: 2, resource: 3 },
        hooks: [{ kind: "implement_bank_or_production", bank: 2, resource: 3 }],
    },
    // Church of Penguin (catalogue "35"): the Disciple is a whole fortress of
    // its own — `alwaysBuildable` makes it implementable any number of times
    // from outside the market, so the Church's "gain an action" is the ordinary
    // Implement Fortress action pointed at a card no deck holds. Conversions
    // happen at the round-end seam and each puts a token here; three wins.
    "church-of-penguin": {
        alwaysBuildable: ["disciple"],
        winAtTokens: 3,
        hooks: [{ kind: "round_end_convert_fortresses", fromCardId: "disciple" }],
    },
    // The Disciple itself carries no conversion logic — the Church reads its
    // position. It is free (cost [0,0,0,0]; the three Bridges under it are the
    // real price), destructible like a building rather than hardened, and it
    // takes its builder out of the round the moment it rises.
    disciple: {
        destructible: {},
        passAfterImplement: true,
    },
};
/** Attach every card's effect configuration; a missing entry is an authoring
 * error caught at module load (each card must declare its behavior, even if
 * a future card's entry is `{}` = deliberately inert). */
function withEffects(cards) {
    return cards.map((c) => {
        const effects = CARD_EFFECTS_CURRENT[c.id];
        if (!effects) {
            throw new Error(`No effect configuration for fortress card "${c.id}"`);
        }
        return { ...c, effects };
    });
}
/** Win cards the 2026-10-17 sheet (catalogue "50") took off the deck. Their
 * effect configs stay in `CARD_EFFECTS_CURRENT` for the matches pinned to the
 * catalogue versions that shipped them. The Church's Disciple is archived
 * alongside (`archivedUndealtCards`). */
const archivedWinCards = [
    win("bam-exploder", "BAM Exploder", 
    // 16 → 18 obsidian (2026-08-21 revision, second pass).
    18, "You may build infinite Artifacts of Courage. Doing so implements the full effect of Artifact of Courage. Your Artifact of Courage fortresses destroy all fortresses (including itself) within their attack range at the end of the round. Place a token on this fortress for every destroyed fortress. Win the game at 3 tokens.", 
    // Two barracks (stars until the 2026-08-21 second pass) joined by two
    // bridges side by side: the 2-edge path between them, both edges bridged
    // — plus two X markers: one on the node where the two bridges meet, one
    // adjacent to it (away from the barracks).
    pat([
        b("barracks"),
        b("barracks", "S", "SE"),
    ], [
        ["S"],
        ["S", "SW"],
    ], [
        br("S"),
        br("S", "SE"),
    ])),
    win("stab-pole-hole", "Stab Pole Hole", 12, 
    // Unprinted availability rule (deliberately not in this text): the
    // action only offers itself while an opponent Barracks can actually
    // reach one of the owner's buildings — the server rejects a designation
    // with no attackers in range, and the client's tray button mirrors it.
    "If another player meets a win condition while this fortress is intact, postpone their victory: play one whole round on your own, from round start to round finish, taking 2 turns in it. Their win condition executes when that round ends, unless you meet your own win condition first.", 
    // A star keep flanked by its two garrison Barracks, four X markers
    // fanning out behind each flank — the "kick me" sign every rax in range
    // is compelled to answer.
    pat([
        b("wild"),
        b("barracks", "NE"),
        b("barracks", "S"),
    ], [
        ["NE", "N"],
        ["NE", "SE"],
        ["S", "SE"],
        ["S", "SW"],
    ])),
    win("church-of-penguin", "Church of Penguin", 
    // 15 → 17 obsidian (2026-09-26 revision).
    17, "Gain an action: build a Disciple — one Barracks of yours with all three of its paths bridged — for free. You must pass for the rest of the round immediately after building a Disciple. At the end of the round, every opponent fortress with a node inside your Barracks defend range of a Disciple is converted to you: you take the card exactly as it stands, tokens and all, along with its pieces on the board. Place a token on this card for each fortress converted, and any Disciple that converted at least one self-destructs. Disciples can be destroyed. Win at 3 converted fortresses.", 
    // The chapel: the steeple (a Barracks, absorbed into the fortress so it
    // may only defend) over a marked crossing, a star chapel at the foot of
    // each aisle, and the transept windows either side of the steeple. The
    // aisle bridges dropped (2026-09-10 revision) for a single nave bridge
    // running UP the steeple's own edge, from the marked crossing to the
    // Barracks; the two Scout chapels became wilds in the same pass. Three
    // buildings, one bridge and three X's.
    pat([
        b("barracks"),
        b("wild", "S", "SE"),
        b("wild", "S", "SW"),
    ], [
        ["NE"],
        ["NW"],
        ["S"],
    ], [
        br("S"),
    ])),
];
/** Archived cards, effects attached — not part of any deck, exported so the
 * catalogue editor (and anyone auditing an old match) can still name them. */
export const FORTRESS_ARCHIVED_CARDS = withEffects([
    ...archivedRegularCards,
    ...archivedUndealtCards,
    ...archivedWinCards,
]);
export const FORTRESS_CARDS_CONFIG = {
    // Catalogue "48": back to 5 face-up cards per row (was 6 from "10").
    regularMarketSize: 5,
    winMarketSize: 5,
    cycleCostPerRegularCard: [0, 1, 0, 0],
    cycleCostPerWinCard: [0, 1, 0, 0],
    // Catalogue "42": back to 2 (was 3 from "22"); "10"-"21" also ran at 2 and
    // everything earlier at 1. Matches keep whatever their pinned catalogue
    // snapshotted.
    roundStartCycleMaxPerRow: 2,
    regularCards: withEffects(regularCards),
    // The undealt Spy rides in the win list so it travels inside the same
    // catalogue document and reaches the room's card lookup; `dealDeck` filters
    // it out of the deck itself.
    winCards: withEffects([...winCards, ...undealtWinCards]),
};
//# sourceMappingURL=fortress.js.map